/**
 * overlays.ts — draw the geometry the API computed. Compute nothing.
 *
 * Every number here arrived from Python via /api/overlays/... . There is no
 * fractal width, no ATR multiple, no fib ratio and no threshold in this file,
 * and there must never be: if the chart computed its own version of a rule, a
 * production bug would still look perfect on screen and a bug here would look
 * like a production bug. Geometry in, pixels out.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { BreakSegment, Zigzag, ZigzagVertex } from './breakprimitive';

export type { BreakSegment, Zigzag, ZigzagVertex };

export type Pivot = {
  idx: number;
  time: UTCTimestamp;
  kind: 'high' | 'low';
  price: number;
  body_price: number;
  wick_price: number;
  confirm_idx: number;
  confirm_time: UTCTimestamp | null;
  confirmed: boolean;
  /** 1 = intermediate, 2 = MAJOR. A degree-2 pivot IS its degree-1 self. */
  degree: 1 | 2;
  /** Degree-2 only: a right-edge major awaiting its confirming break.
   *
   *  NOT "the open phase's running extreme", despite `ict/mss.py`'s docstring saying so
   *  — `mss` walks the SWING SEQUENCE, so this is the running extreme of the swings that
   *  have PRINTED. The in-progress leg is a different object (`open_leg`). */
  major_pending: boolean;
  /** t_16cb0512: a structural point (degree 2) with no degree-1 swing on its bar. A MARKER
   *  only: `swingZigzag` skips it, so the drawn zigzag stays the alternating degree-1 walk. */
  structural_only?: boolean;
  /** Set when the OPEN LEG has already priced through this pending major (measured on
   *  31.6% of cached series). Carries the leg's price, so the chart can show BOTH
   *  numbers rather than silently choosing one. */
  superseded_by_open_leg?: number;
  /** This swing's leg began before bar 0 — extreme of the VISIBLE part only. */
  edge: boolean;
};

export type SwingsPayload = {
  component: 'swings';
  source: string;
  params: Record<string, never>;
  /**
   * The leg IN PROGRESS after the last confirmed pivot, or `null` when there is
   * none. This replaced a `counts.unconfirmed` field that was STRUCTURALLY unable
   * to be non-zero (`alternating_swings` sets `confirm_idx` to the current bar of
   * its own walk), so it displayed a reassuring `0` while every series had a 1-25
   * bar unlabelled tail. This one can be absent and can move.
   */
  open_leg: OpenLeg | null;
  counts: {
    total: number;
    highs: number;
    lows: number;
    /** Closed bars since the last confirmed pivot. Never 0 in practice (median 3). */
    open_leg_bars: number;
    pct_of_bars: number;
    /** 0 == the sequence strictly alternates high/low/high/low. */
    alternation_violations: number;
    /** Bars that are BOTH a swing high and a swing low. 0 by construction. */
    bars_both_high_and_low: number;
    confirm_lag_median: number | null;
    confirm_lag_max: number | null;
    majors: number;
    major_highs: number;
    major_lows: number;
    /** The PAIRED REDUCTION gate, visible: ~14% cache-wide, 100% would be a no-op. */
    majors_pct_of_swings: number;
    /** Right-edge majors awaiting a structural break. Small by construction. */
    majors_pending: number;
    /** Of those, how many the OPEN LEG has already priced through. 31.6% of series. */
    majors_pending_superseded?: number;
    major_alternation_violations: number;
  };
  pivots: Pivot[];
};

/**
 * The running extreme of the leg that has not ended yet — a THIRD visual state,
 * neither confirmed swing nor absent.
 *
 * It is not a Pivot and must never be styled as one: no `confirm_idx`, it can move
 * on the next close, and it may never become a swing at all. Drawn as the zigzag's
 * LAST VERTEX — a hollow arc on `BreakPrimitive` (hollow = unsettled, fact 2) with a
 * DASHED final segment reaching it (fact 4). RETIRED 2026-08-23 (t_13260999): the
 * hollow marker-layer circle with a `?` suffix, together with every other text label.
 */
export type OpenLeg = {
  kind: 'high' | 'low';
  idx: number;
  time: UTCTimestamp;
  price: number;
  /** Closed bars in the leg so far. */
  bars: number;
  from_idx: number;
  provisional: true;
  note: string;
};

/**
 * Degree-2 colour. It USED to double as the sidebar legend, and since 2026-08-23
 * (t_13260999) it does not: the pivots carry degree on VERTEX RADIUS in the zigzag, so
 * nothing this colour is on is what the `majors` count row counts. Two chart channels
 * are left — the `confirm_time` circle in `swingMarkers` (behind `show-confirm`, and on
 * the confirming bar rather than the pivot's own) and the degree-2 dealing-range edges
 * (`RANGE_COLORS`, two marks whatever the count is). `main.ts`'s `kv` still tints any
 * `major*` sidebar key with it, which is a within-sidebar grouping cue, not a claim
 * about a mark on the chart.
 */
export const MAJOR_COLOR = '#ffd54f';
/**
 * Degree-2, right edge, awaiting a structural break. Dimmed. Used at ONE site — the
 * `confirm_time` circle in `swingMarkers` — since the printed-position marker it also
 * tinted was retired 2026-08-23 (t_13260999) along with its `?` suffix.
 */
export const MAJOR_PENDING_COLOR = '#8a7320';
/**
 * The OPEN LEG's running extreme — a third state, distinct from both a confirmed
 * swing and from nothing. Blue-grey so it cannot be mistaken for either degree's
 * palette. Used at ONE site — `openLegDrawing`'s ray — since 2026-08-23
 * (t_13260999): the marker it also coloured (a circle with `?`) is now the zigzag's
 * last vertex, drawn in `ZIGZAG_COLOR` by `BreakPrimitive` like every other vertex.
 */
export const OPEN_LEG_COLOR = '#5c7cfa';

/**
 * THE SWING ZIGZAG'S ONE COLOUR AND ONE ALPHA (2026-08-23, card t_e383d6f8).
 *
 * Andrei: "тонкие полупрозрачные линии как в индикаторе zigzag, выбери цвет и прозрачность так,
 * чтобы линии не сбивали с чтения графика и не создавали ощущение перегруженности." So the choice
 * is mine, and it is MEASURED on real panes rather than eyeballed once: the numbers below come
 * from 1W / 1D / 15m of live BTC through the real app, 12 candidates, evidence archived at
 * `docs/reports/evidence/zigzag_colour_2026-08-23/`.
 *
 * WHITE AT 0.60, NOT A GREY, and the grey is the interesting half. Every mid-grey that is legible
 * over 15m candles lands on top of a tone this chart ALREADY assigns a meaning to: composited over
 * `CHART_BG` (#131722), `rgba(150,155,170,0.70)` is dE 3.7 from `#787b86` and
 * `rgba(209,212,220,0.55)` is dE 1.9 — and `#787b86` is simultaneously the dealing range's `eq`
 * edge and an unconfirmed pivot (it was also the IDM level until card t_0cdae4dc moved that to
 * `IDM_LEVEL_COLOR` magenta, and the week box's neutral border until card t_70188040). Under dE ~5 is the same colour
 * to a reader, so a grey zigzag would be one more meaning in one tone.
 * White at 0.60 composites to #a1a2a7: dE 15.4 from that grey, a full step of tone apart
 * while staying in the same neutral family.
 *
 * IT IS NOT A RED OR A GREEN, which is the card's hard constraint — a hued line reads as candle
 * direction. This is achromatic BY CONSTRUCTION rather than by eye: pure white over a
 * near-neutral background has a channel spread of 6, against 13-18 for every grey tried (those
 * carry a blue cast). Measured distance from the candle hues: dE 37.0 from #26a69a and 70.7 from
 * #ef5350, and from every other signal colour on the chart (major yellow, accent blue, open-leg
 * blue, the structure dots) 52.8 or more.
 *
 * WHY 0.60 AND NOT LOWER. The 15m pane is the binding case (Andrei's screenshot: ~10 swings on
 * screen), and the test is not "is the line there" but "is it quieter than the candles". Measured
 * as mean channel delta of the pixels the line changed, against the candles' own delta from the
 * background on the same pane: 0.658 of candle contrast on 15m, 0.743 on 1D, 0.605 on 1W. Below that
 * the line stops surviving the 15m candle mat — at alpha 0.35 it is 0.23 of candle contrast and
 * four independent readings of the shot agreed it dissolves into the cluster; above ~0.85 it
 * starts to compete with the candles, which is exactly what Andrei asked against.
 *
 * ONE VALUE, no per-case cocktail: degree and confirmation state are carried at the VERTEX by
 * `BreakPrimitive._drawZigzag`'s own marks (radius `ZZ_R_MINOR`/`ZZ_R_MAJOR` for degree,
 * `stroke()` vs `fill()` for settled), not by the line, so the polyline has one job and one
 * tone. NOT by `structureDots`, which this file exports, unit-tests and calls from nowhere —
 * see the dead-function note in fact 1 below. See the ZIGZAG DECISION block on `swingMarkers`.
 *
 * ONE THEME SHIPS. Verified rather than assumed: `chartapp/web/index.html` has a single `:root`
 * palette, no `prefers-color-scheme` block and no theme toggle anywhere in `chartapp/web/src`,
 * and the canvas background is the one `CHART_BG` constant. There is no light-theme variant to
 * check this against, and a second constant for a theme that does not exist would be the
 * speculative config this repo keeps deleting.
 *
 * ponytail: an alpha fitted to BTC on three panes, and the 0.35..0.85 contrast band is my own
 * threshold, not a rule from anywhere. If Andrei says crowded, move the alpha, not the hue —
 * the hue is the constrained half.
 */
export const ZIGZAG_COLOR = 'rgba(255, 255, 255, 0.60)';

/** Hairline. A zigzag is scaffolding; the candles are the subject. */
export const ZIGZAG_WIDTH = 1;

/**
 * Swing markers — since 2026-08-23 (t_13260999) this function draws the `confirm_time`
 * CIRCLE AND NOTHING ELSE. Read the "IMPLEMENTED" paragraph at the foot of this block for
 * what is left and why; everything between here and the ZIGZAG DECISION is the encoding that
 * was DELETED, kept in the PAST TENSE because DONE item 2 of that card requires the record to
 * say what stopped being visible rather than to go quiet.
 *
 * TWO MARKERS PER PIVOT, and the second one is the point of this whole app. That is still
 * true, only the first half moved layers: a fractal pivot at bar p is not knowable until bar
 * p+n has CLOSED, so the chart draws where it PRINTED (now a ZIGZAG VERTEX on
 * `BreakPrimitive`, at the pivot's own price) and, optionally, where it became INFORMATION
 * (the small circle below, at `confirm_time`). The horizontal gap between the pair is the
 * look-ahead a naive consumer would steal — made visible instead of argued about.
 *
 * RETIRED — the arrow-and-text encoding this function used to emit. None of it is drawn any
 * more; the four facts it carried are accounted for in the ZIGZAG DECISION block below.
 *   - `shape: 'arrowDown'/'arrowUp'` on the printed bar, in red `#ef5350` / teal `#26a69a`.
 *     Gone: the printed position is a vertex, and no arrow is emitted from the swing path.
 *   - `MH`/`ML` (degree 2) and `SH`/`SL` (degree 1) text. Gone: this whole card exists to
 *     delete it (Andrei, "текущие ML/MH и SL/SH ... я бы удалил совсем, чтобы лучше
 *     читалось"). Degree is now vertex RADIUS.
 *   - The `labelled` parameter that gated the degree-1 text at window=1, where 40% of bars
 *     are pivots and "BOSSH"/"SHSHSH" runs were unreadable. DELETED with the text, not left
 *     as a dead argument — see the foot of this block.
 *   - A dimmed colour standing in for a shape the marker layer cannot draw, for a pivot that
 *     had printed but was not yet knowable. Gone twice over: this function now SKIPS
 *     unconfirmed pivots outright (`if (!p.confirmed …) continue`), and hollow is literally
 *     hollow on the primitive (`ctx.stroke()`).
 *   - A trailing `?` on a pending major, and a `!` on one the open leg had already priced
 *     through. The `?` is the hollow vertex; the `!` moved to the ray plate (fact 3 below).
 *     Dimming survives, on this function's confirm circle only (`MAJOR_PENDING_COLOR`).
 *   - `size: isMajor ? 1.2 : 0.7`. It never rendered as two sizes at any shipped pane
 *     geometry — measured, see the size-quantisation paragraph in the decision — so degree on
 *     the old chart was carried entirely by HUE. `size` here is now a flat 0.4.
 *
 * DEGREE 2 IS NOT A SECOND MARKER, and that constraint outlived the encoding: a major IS its
 * degree-1 self (selection, never invention), so stacking a second mark on the same candle
 * would draw one formation twice — exactly the double-counting the alternating sequence exists
 * to remove. It is why degree is an ATTRIBUTE of the one vertex (radius) and not an extra
 * object, and why fact 1 below refuses a second polyline over the majors.
 *
 * ZIGZAG DECISION (2026-08-23, card t_a1ee0dcd; rewritten same day after redteam round 1
 * refuted two premises of the first draft BY MEASUREMENT -- probes and raw output in
 * process/reviews/t_a1ee0dcd/, 117 cached series, source="bars_cache") -- where each of the
 * four facts this function encodes in MH/ML/SH/SL text goes once the arrows and labels are
 * deleted and a zigzag polyline over the swing points replaces them.
 *
 * THE RULE THE MEASUREMENT FORCED, and the whole decision follows from it: DEGREE and
 * CONFIRMATION ARE PROPERTIES OF A VERTEX, NOT OF A SEGMENT. The first draft put both on
 * the line and both came out as false claims about the candles (numbers below). So the line
 * spends exactly ONE dimension, on the one fact that genuinely IS a segment, and vertex
 * facts stay at the vertex.
 *
 *   LINE   dash, on exactly ONE segment: the open leg (fact 4). Nothing else. One polyline,
 *          one width, one neutral tone, one alpha -- geometry only.
 *   VERTEX size = degree (fact 1); filled vs hollow = settled vs not (fact 2).
 *
 * That is ONE line-level dimension against a budget of two, and it is cheaper than the
 * draft it replaces rather than a workaround: two vertex attributes on marks that already
 * have to be drawn cost no extra objects, while the draft cost a second polyline.
 *
 * THE LAYER, AND IT IS PART OF THE DECISION (added after redteam round 2, which EXECUTED
 * lightweight-charts 4.2.3's own marker geometry -- probes in process/reviews/t_a1ee0dcd/).
 * Both vertex encodings must be drawn BY THE CANVAS PRIMITIVE that draws the polyline (the
 * `breakprimitive` pattern: its own `ctx.arc()`), NOT by `SeriesMarker`. On the marker layer
 * both facts are INEXPRESSIBLE, measured rather than argued:
 *   - SIZE quantises away. `size(barSpacing, coeff)` clamps barSpacing to MinShapeSize=12,
 *     so `calculateShapeHeight` pins at 10 at EVERY shipped pane geometry (15m barSpacing
 *     0.52..2.31, 1D 4.10..8.20, 1W 3.21..6.41, 1M 4.17..8.33), and `ceiledOdd` then maps
 *     multipliers 1.2 and 0.7 to the SAME 9px circle (arrows: 11px both). Separation needs
 *     multiplier >= 1.26 (1.25 -> 9, 1.26 -> 11) or barSpacing above the clamp (~18.2, only
 *     reachable at ~2220px pane width on 1D).
 *   - HOLLOW does not exist there. `SeriesMarker` has no fill/stroke/border/opacity field;
 *     `drawCircle` is `arc()` then `fill()`, with no `stroke()` anywhere on the marker path.
 *     That is why the RETIRED list above records unconfirmed as drawn "hollow-ish via a
 *     dimmed colour" -- a colour standing in for a shape it cannot draw.
 * On the primitive both are one line each: `ctx.arc(x, y, r, 0, 2*PI)` then `fill()` for
 * settled, `stroke()` for not. Vertex radii are therefore REAL PX under our control, and the
 * implementation must state them as such: minor r = 2 * hr, major r = 4 * hr (a 2x radius,
 * 4x area -- well clear of the 1.26 quantisation floor that killed the marker version), with
 * `lineWidth = Math.max(1, Math.round(1 * hr))` for the hollow stroke, matching how
 * `breakprimitive` already scales every dimension by the bitmap ratio.
 *   AND THE ENCODING BEING ADOPTED IS NOT A WORKING PRECEDENT. This function's `size: isMajor
 *   ? 1.2 : 0.7` has NEVER rendered as two sizes at any shipped geometry (arrow 11px both).
 *   Degree on today's chart is carried entirely by COLOUR (`MAJOR_COLOR` yellow). So the move
 *   is not "preserve a working channel"; it is "adopt a channel that only starts working once
 *   we own the drawing". If any part of the vertex marks is ever left on `SeriesMarker`, both
 *   facts are silently lost while looking implemented -- which is the one failure this card
 *   exists to prevent, so `t_b87831db` must move the vertex marks off that layer, not just
 *   the polyline.
 *   COLLISION NOTE (non-blocking, measured): pivots that coincide with an existing
 *   `structurePointDots` mark on the same bar+side are 290/7201 (4.0%) cache-wide, 288/1329
 *   (21.7%) for majors. Marker-layer `aboveBar`/`belowBar` marks stack with a growing gap, so
 *   a primitive vertex and a marker dot at the SAME price will sit at different y. Expected,
 *   not a bug: the primitive draws at the price, the marker stacks off it.
 *   AND THE SIZE CHANNEL DOES NOT SEPARATE AT 15m DENSITY (redteam t_1bc8ba85 D2, MEDIUM, from
 *   the drawn coordinates at zoom zero): a major's disc is 8 CSS px across and the median
 *   nearest mark at 15m is 4.32 CSS px away, so 29 of 36 majors have another mark's centre
 *   INSIDE their own footprint and only 6 of 36 keep a clean silhouette. On 1W/1D it separates
 *   perfectly (26/26 and 15/15, zero overlap, median nearest 10.93/25.03 CSS px). The honest
 *   accounting this block owes: the retired encoding this paragraph above compares against
 *   (`size: 1.2` vs `0.7`) never rendered as two sizes, but the encoding actually SHIPPING at
 *   `e9716bb` carried degree on HUE (`MAJOR_COLOR`), which is density-INDEPENDENT. So the move
 *   is a strict improvement on 1W/1D and a REGRESSION at 15m, which the decision as written did
 *   not acknowledge. Not repaired by adding hue back -- hue is the one channel Andrei spent on
 *   SIDE (2026-08-10) and re-spending it is a new decision, carded.
 *   REPAIRED 2026-08-23 (card t_4a1010f9) BY THINNING THE MARKS, NOT BY A NEW CHANNEL --
 *   `breakprimitive.planVertexMarks`, whose own block carries the sweep. A settled MINOR mark
 *   whose disc would land on an already-drawn disc is withheld; a MAJOR and a HOLLOW mark never
 *   are, because those are the marks that CARRY facts 1 and 2. Re-measured on the same
 *   `geom.json` D2 came from (`process/reviews/t_4a1010f9/`), counting only marks actually on
 *   screen: 15m majors clean 6/36 -> 30/36, a neighbour's centre inside the disc 18 -> 2, median
 *   nearest 4.32 -> 7.86 CSS px, and the 2 survivors are overlapped by another MAJOR, which no
 *   minor-thinning can reach and which misreads as nothing (the reader still sees a large disc).
 *   D3's crowding falls with it in ONE change, as the card required. PREDICTED first, from the
 *   parent card's stored geometry (227 arcs at that capture): marks on screen 227 -> 144, marks
 *   overlapped by a neighbour 147 (64.8%) -> 6 (4.2%), D3's own x-gap reading 179/226 (79.2%) ->
 *   73/143 (51.0%), x-interval vertex ink 76.6% -> 70.5%. Then CONFIRMED on the rendered canvas
 *   (226 arcs -- one pivot fewer, a later capture of a live feed), which reproduced every one of
 *   those numbers to the arc. THE LINE IS UNTOUCHED -- every
 *   convertible vertex is still joined at its own price, so a withheld mark's position is still
 *   on the chart as the polyline's corner, exactly as `planLabels` withholds a redundant word
 *   and never a segment. AND IT IS NOT A TIMEFRAME RULE: there is no `tf` argument and no
 *   density threshold, so on 1W/1D it suppresses NOTHING (0 of 40 and 0 of 27 marks on screen)
 *   and those panes are byte-identical. Zoom in on 15m and the marks come back.
 *   AND WHAT IT DOES *NOT* FIX, stated because the evidence forced it and a reader looking at the
 *   AFTER screenshot will otherwise think the measurement lied. Re-measured on the RENDERED
 *   canvas (`docs/reports/evidence/zigzag_15m_thinning_2026-08-23/`, a real Chromium at dpr 2,
 *   both shots from private worktrees of ca0fc62 on the SAME 225-pivot payload): marks 226 -> 143
 *   and every D2 number above reproduces, but the zigzag's composited INK falls only 3.6% and an
 *   independent eye-read of the two PNGs described BOTH as a merged chain in the flat left band.
 *   Both are true, for two measured reasons (`why_ink_barely_moved.py`): a withheld disc was
 *   already INSIDE its neighbour, so the disc UNION shrinks 15.2% where the COUNT shrinks 36.7%
 *   -- D2 is a question about a BOUNDARY, not about area -- and in that band 225 segments join 226
 *   pivots, so the dominant ink is the POLYLINE, whose 4,832 px are byte-identical before and
 *   after because geometry is never withheld. So this card fixes the DEGREE CHANNEL and reduces
 *   the marks; it does not make a dense band sparse, and it cannot: that would be a decision
 *   about drawing fewer LEGS, i.e. the detector's pivot set or a second line-level dimension,
 *   both of which this card's DO NOT list forbids. D3 is therefore PARTLY closed, not closed.
 *
 * fact 1 -- DEGREE. Lives on VERTEX SIZE (major mark larger), NOT on line width.
 *   REFUTED (first draft, and this is why): the draft drew a second, thicker polyline over
 *   the majors only, arguing from the comment above -- "A major IS its degree-1 self
 *   (selection, never invention)" -- that this is a subset path so no formation is drawn
 *   twice. True of the POINTS, false of the LINE. Consecutive majors are generally NOT
 *   adjacent pivots: of 1214 major segments cache-wide, 944 (77.8%) skip at least one minor
 *   pivot, median 4 skipped, max 44. So the thick line is a CHORD across the thin one --
 *   two lines at different angles over the same price region, i.e. exactly the visual noise
 *   this card exists to remove, and worse than the two marker sizes it replaces. Do not
 *   reintroduce the word "subset": a later reader will implement a subset polyline and get
 *   chords.
 *   Vertex size is where a per-pivot property belongs, and it costs no line dimension.
 *   NOT the structure dots: `structureDots` in this file does size-by-degree (1.4/0.6) and
 *   is DEAD -- exported, unit-tested, called from nowhere. `main.ts` draws
 *   `structurePointDots` (chartpane.ts): one uniform size, hue by SIDE, placed on inducement
 *   EVENTS rather than pivots, and its own history block records degree-by-size-and-hue as
 *   tried and "wrong", retired on Andrei's instruction. The zigzag's vertices are its own
 *   marks, so this is a new object's size channel, not a revival of that convention.
 *
 * fact 2 -- `?` PRINTED BUT NOT YET SETTLED. Lives on VERTEX FILL (hollow = not settled),
 * NOT on a dash shared with fact 4.
 *   REFUTED (first draft): it argued the unsettled pivots and the open leg are "the same
 *   contiguous region of the chart by construction", so one dash covers both. Measured, both
 *   halves are false for the dominant case. Cache-wide there are ZERO `confirmed:false`
 *   pivots, and redteam round 2 showed this is STRUCTURAL rather than merely rare:
 *   `alternating_swings` sets `confirm_idx` from a bar that actually traded, so
 *   `confirm_idx <= last` always -- `max(confirm_idx - last) = 0` over 2,998 synthetic
 *   series. So `?` is ALWAYS a `major_pending`, and fact 2's encoding only ever has to
 *   describe a pending major -- and that vertex is INTERIOR in 78 of 121
 *   cases (last pivot only 31.4% of the time; pivots printed after it median 1, max 18).
 *   A trailing dash would therefore dash settled, confirmed minor pivots because a major
 *   BEHIND them is unbroken: a false claim about those pivots, and the opposite of the
 *   honesty the dimmed-colour rule above is quoting. A hollow vertex is correct wherever the
 *   pending major sits, interior or not, and says nothing about its neighbours.
 *   Fill, not colour: colour on this chart is already spent (candle red/teal refused
 *   outright, structure dots spend hue on SIDE per Andrei 2026-08-10).
 *
 * fact 3 -- `!` A PENDING MAJOR THE OPEN LEG HAS ALREADY PRICED THROUGH. DROPPED from the
 * chart marks; it lives on the open leg's ray plate ONLY. `openLegDrawing` already prints
 * "beats MH?/ML? <price>" beside the live extreme, and the `!` was never the information --
 * it was a TELL that a comparison was needed (measured on 31.6% of cached series by the
 * retired `!` suffix recorded above; the reader must not compare a stale printed price to
 * the candles by eye). The plate does that comparison and names both prices at the price the
 * leg reached, which is strictly more than a punctuation mark on a vertex.
 *   CARRY THIS INTO THE IMPLEMENTATION, since the plate becomes the ONLY carrier: the retired
 *   `!` flagged EVERY outrun major, while `openLegDrawing` names ONE via a single
 *   `find()`, so a second same-kind superseded major would be dropped silently. Measured
 *   safe today -- 36 superseded majors across 117 series, 0 multi, 0 opposite-kind -- which
 *   is consistent with the api's own same-kind guard being documented as unreachable. If a
 *   multi case ever appears, the plate must list them or this fact goes partly invisible.
 *   WHAT IS GENUINELY LOST, stated rather than glossed: the outrun major's own vertex no
 *   longer flags itself, so the fact is only visible where the ray is. That is acceptable
 *   because both are behind the SAME gate -- `main.ts` builds the swing markers and calls
 *   `openLegDrawing` inside one `if (draw.pivots)`, so no toggle state shows the zigzag
 *   without the plate. If that gate is ever split, this fact goes invisible and this
 *   paragraph is the reason to re-open it.
 *
 * fact 4 -- THE OPEN LEG. The ONE line-level dimension, and it is a segment fact rather than
 * a vertex one: the final segment (last pivot -> running extreme) is DASHED, everything
 * behind it solid. The dash is a DASH, not a fixed literal -- `breakprimitive.zigzagDash`
 * owns the period and scales it to the segment carrying it (see the MEASURED AFTER SHIPPING
 * paragraph below, which is what retired the literal). It is a true claim about that segment
 * and only
 * that segment -- unlike the draft's trailing dash, which would have reached back over
 * settled pivots (fact 2). The hollow circle and the ray both STAY: the circle says WHICH bar
 * set the extreme, the ray carries the number. Before this marker existed the newest 1-25
 * bars (median 3) carried nothing at all -- the xyz:SNDK 1D complaint -- so the dashed
 * segment must reach the live extreme, not stop at the last confirmed pivot.
 *   NOTE the vocabulary collision the implementation must keep clean: an open-leg vertex is
 *   hollow for fact 2 AS WELL, because the running extreme is by definition not settled. That
 *   is one object honestly carrying both facts (unsettled vertex, unsettled segment), not two
 *   encodings of one.
 *   MEASURED AFTER SHIPPING, AND THE CHANNEL WAS UNDER-SIZED ON HALF THE 1W PANES -- FIXED
 *   2026-08-23 BY SCALING THE PERIOD (redteam `t_1bc8ba85` D1, HIGH, on the rendered canvas at
 *   ZOOM ZERO; closed by card `t_fce34e7f`). The dash period was a FIXED 9 CSS px
 *   (`[5 * hr, 4 * hr]`) and the open leg's own segment was SHORTER than one period on 28 of 57
 *   1W series, 3 of 41 1D and 6 of 17 15m -- BTC 1W measured 0.93 periods (16.7 bitmap px), so
 *   it rendered as a solid stub with no interior gap POSSIBLE. 49% of 1W series drew under one
 *   period, 68% of 1W+1D under two. The fact was not silently lost -- `swingsSummary`'s
 *   `open leg` row states it in words and the ray plate carries the price -- but on those panes
 *   the LINE half of fact 4 was not perceivable, so the claim "the segment and its ray read as
 *   one object" was false there. The parent card's own probes only ever shot BTC 15m/1D, where
 *   the median is 11.65/12.93 periods, i.e. the pane where the channel failed is the one neither
 *   probe measured.
 *   THE FIX IS `breakprimitive.zigzagDash`, whose docstring carries the arithmetic: a segment
 *   with room for >= 2 periods at the base 9px keeps the base 9px EXACTLY, a shorter one gets
 *   `length / 2` and therefore shows exactly two periods, with a 2px floor because sub-pixel
 *   ink is a smear rather than a dash. THE "KEEPS THE BASE" HALF IS A PROPERTY OF SEGMENTS AND
 *   NOT OF PANES, and a first draft of this paragraph said "so 1D/15m are byte-identical",
 *   which is FALSE against this card's own evidence rows (redteam `t_fce34e7f` F1): measured
 *   after the fix, 1D keeps the base period on 20 of 20 series (median 7.324, min 2.270) while
 *   15m keeps it on 9 of 13 — AAVE, AERO, ACE and ANIME drew 1.134/1.186/1.033/0.989 periods
 *   and are re-encoded, which is the fix working rather than a pane being disturbed. Any pane
 *   can hold a short leg. Re-measured on the RENDERED canvas over >= 40 1W series after the
 *   fix, evidence in `docs/reports/evidence/zigzag_dash_2026-08-23/`.
 *   THE TWO SWEEPS' MEDIANS ARE NOT COMPARABLE AND THAT IS ARITHMETIC, NOT A DISCREPANCY
 *   (redteam `t_fce34e7f` F2): D1 divided the leg's HORIZONTAL EXTENT (`open_leg.bars` x
 *   px-per-bar) by the period, while the after-fix sweep divides the DRAWN HYPOTENUSE recorded
 *   off the canvas — and the vertical component is large, median hypotenuse/x-extent 1.69 on
 *   1W and 3.53 on 1D. Recomputing D1's own quantity on the after-fix rows gives 1.59 (1W) and
 *   1.65 (1D) periods against D1's published 1.48 and 1.55, i.e. the two agree once the same
 *   quantity is compared; the 4.7x on 1D is the hypotenuse, not a different pane.
 *   A SHORTER FIXED LITERAL WAS THE OBVIOUS CANDIDATE AND IT DOES NOT WORK: the shortest legs
 *   measured 0.30 of a period, i.e. 2.7 CSS px, so even `[2, 2]` is longer than the whole
 *   segment and still draws a stub. No constant can beat a quantity that varies by three orders
 *   of magnitude across the panes.
 *   AND THE "ONE SHARED LITERAL" OBJECTION WAS FALSE AT THE PRIMARY SOURCE: the ray is drawn by
 *   `RangePrimitive._drawRays` as `[4 * hr, 4 * hr]` (8px) against this file's 9px, so the two
 *   never shared a literal at all. What makes them read as one object is the shared price, the
 *   shared hollow tip and the shared dashed-ness. The BREAK segments keep their own 9px literal
 *   untouched -- `zigzagDash` is reached only from the zigzag's tail.
 *
 * CONSEQUENCES FOR THE IMPLEMENTING CARD (t_b87831db): the dashed segment's far endpoint
 * tracks the LIVE candle, never the detector refresh (R4/D1 -- the week band was measured a
 * whole 1D bar stale on the 1D pane) -- IMPLEMENTED as `zigzagLiveTip` + `paintZigzagTip`,
 * which re-aim the tail from `pane.bars` on every candle write exactly as `paintWeekBox` does
 * for the band; the payload cannot supply it, `_open_leg` being closed-bars-only by audit
 * HA-5. And `swingMarkers`' `labelled` parameter dies with the
 * text it gated rather than surviving as a dead argument. Symbol names, not line numbers, in
 * anything written here: the first draft's ":158"/":186-193" citations were stale within one
 * insertion of being written.
 *
 * IMPLEMENTED 2026-08-23 (t_b87831db), and what is LEFT of this function is the point:
 * everything the decision put on a VERTEX or on the LINE moved to `swingZigzag` below, drawn
 * by `BreakPrimitive`. All that remains on the marker layer is the `showConfirmations`
 * circle, which the decision was silent on (recorded as a gap in this card's instruction by
 * the reviewer) and which is NOT one of the four facts: it marks a DIFFERENT bar
 * (`confirm_time`, where the pivot became information), so it is a second point in time
 * rather than an attribute of the vertex, and the two marker limits the decision measured --
 * size quantisation and no fill/stroke -- do not bind on it (one size, filled). The pivot's
 * own printed position is now the zigzag vertex, so the look-ahead gap this comment is about
 * is still drawn as a PAIR: vertex on the primitive, circle on the marker layer.
 *
 * AND IT STAYS HERE RATHER THAN MOVING ONTO `swingZigzag`, which the parent card's reviewer
 * pre-registered as the condition for a move (review round 1, non-blocking 1: "the reason block
 * must MOVE with the code if `swingMarkers` becomes an empty-marker shell"). Recorded rather
 * than done, because the two functions are ADJACENT in this one file -- `swingZigzag` is the
 * next declaration below -- so the block is not separated from its subject the way
 * `structureDots`' was from another module; and because a ~130-line comment relocation in a
 * checkout nine agents share is a diff that can only collide (the `staging_accident_shared_
 * checkout` class). The moment either function moves to another file, the block goes with
 * `swingZigzag`, and `breakprimitive.ts`'s citation of it is re-aimed in the same diff.
 *
 * The OPEN LEG's circle moved too, for the reason the decision names: its vertex is hollow
 * for fact 2 as well, and `SeriesMarker` cannot draw hollow. It is the zigzag's last vertex.
 */
export function swingMarkers(
  payload: SwingsPayload,
  showConfirmations: boolean,
): SeriesMarker<Time>[] {
  const markers: SeriesMarker<Time>[] = [];

  // t_edf5f2e4: a `structural_only` entry is a degree-2 structural point with no degree-1
  // swing on its bar, so `swingZigzag` gives it no vertex. Without this it had NO mark at its
  // own bar. It gets one major-coloured disc at `p.time`, ALWAYS (not behind show-confirm:
  // this is the point itself, not its confirmation), pending colour when the point is not
  // settled (the open extreme is `confirmed: false`).
  // DECISION: degree-2 points that DO coincide with a degree-1 pivot keep "degree lives on
  // vertex size" (ZIGZAG DECISION block) -- they are vertices, and a second mark on the same
  // candle is the stacking the decision removed. The disc is only for points with no vertex.
  for (const p of payload.pivots) {
    if (!p.structural_only) continue;
    markers.push({
      time: p.time,
      position: p.kind === 'high' ? 'aboveBar' : 'belowBar',
      shape: 'circle',
      color: !p.confirmed || p.major_pending ? MAJOR_PENDING_COLOR : MAJOR_COLOR,
      size: 1,
      text: '',
    });
  }

  if (!showConfirmations) {
    // The payload appends structural entries; lightweight-charts drops out-of-order markers.
    markers.sort((a, b) => (a.time as number) - (b.time as number));
    return markers;
  }

  for (const p of payload.pivots) {
    if (!p.confirmed || p.confirm_time === null) continue;
    markers.push({
      time: p.confirm_time,
      position: p.kind === 'high' ? 'aboveBar' : 'belowBar',
      shape: 'circle',
      color: p.degree === 2
        ? (p.major_pending ? MAJOR_PENDING_COLOR : MAJOR_COLOR)
        : '#2962ff',
      size: 0.4,
      text: '',
    });
  }

  // lightweight-charts requires markers in ascending time order; confirm bars interleave.
  markers.sort((a, b) => (a.time as number) - (b.time as number));
  return markers;
}

/**
 * THE SWING ZIGZAG — the polyline that replaced the arrows and the MH/ML/SH/SL text.
 *
 * Geometry only. Every encoding decision is in the ZIGZAG DECISION block above and every
 * pixel is drawn by `BreakPrimitive.setZigzag` (that primitive, not a new one: it already
 * has the time->x / price->y conversion, the bitmap-ratio scaling and the off-scale abort).
 * This function's whole job is to turn the payload into vertices in time order.
 *
 * THE OPEN LEG IS THE LAST VERTEX, not a separate object, which is what makes the dashed
 * tail reach the LIVE extreme rather than stopping at the last confirmed pivot — the
 * xyz:SNDK 1D complaint, where the newest 1-25 bars (median 3) carried nothing at all.
 *
 * `pending` is `!confirmed || major_pending`, i.e. BOTH ways a vertex can be unsettled, and
 * the open leg's running extreme is unsettled by definition. Measured: `confirmed:false` is
 * structurally impossible cache-wide (`alternating_swings` sets `confirm_idx` from a bar that
 * traded), so in practice this flag means "pending major" or "open leg" — but the disjunction
 * is written out because a detector change that makes the first reachable must not silently
 * start drawing those vertices as settled.
 */
export function swingZigzag(payload: SwingsPayload): Zigzag {
  // `structural_only` entries (t_16cb0512) are MH/ML MARKERS for structural points with no
  // degree-1 swing on their bar. They are not swing-sequence vertices: drawing them made
  // same-kind adjacent vertices (a HIGH mark on a downward kink) and a zero-length open tail.
  const points: ZigzagVertex[] = payload.pivots.filter((p) => !p.structural_only).map((p) => ({
    time: p.time,
    price: p.price,
    major: p.degree === 2,
    pending: !p.confirmed || p.major_pending,
  }));
  points.sort((a, b) => (a.time as number) - (b.time as number));

  const ol = payload.open_leg;
  if (ol) {
    points.push({ time: ol.time, price: ol.price, major: false, pending: true });
  }
  return {
    points,
    dashLast: ol !== null && points.length > 1,
    liveKind: ol ? ol.kind : null,
    color: ZIGZAG_COLOR,
    width: ZIGZAG_WIDTH,
  };
}

/**
 * THE DASHED TAIL'S FAR ENDPOINT, RE-DERIVED FROM THE CANDLES ON SCREEN.
 *
 * `null` = nothing to replace (no open leg, no bars, or no bar in the leg's window beats the
 * closed extreme, which is the common case).
 *
 * WHY THIS EXISTS. `open_leg` comes from `chartapp/api/overlays.py::_open_leg`, which is
 * CLOSED BARS ONLY and deliberately so (audit 2026-07-04 HA-5 — a live full-size plan was
 * armed off an unclosed candle). That is right for the payload and wrong for the drawn tail:
 * the payload is refetched on ROLLOVER only, so between closes the tail reached a level the
 * candles beside it had already passed.
 *
 * MEASURED WITH THE REAL DETECTOR (`process/reviews/t_b87831db/probe_livetip_magnitude.py`: per series, treat the last
 * cached bar as forming, run `alternating_swings` + `_open_leg` over the CLOSED bars, ask whether
 * the forming bar beats the answer on the leg's own side). 1D: the forming bar extends the
 * open-leg extreme on 13 of 39 series (33.3%), median 2.25%, worst AMD 6.42% / INTC 6.17% /
 * SNDK 4.68% — SNDK being this feature's own origin series; 1W 2/38 (5.3%), 1M 2/34 (5.9%).
 * The review that filed this defect approximated the detector with a 5-bar fractal walk and got
 * 9/39 on the same worst four names: the real 1D rate is a THIRD of panes, not a quarter. Same
 * class as the week band's t_420df4d4 D1, same shape of fix: the drawing follows `pane.bars`,
 * the DETECTOR does not.
 *
 * IT CROSSES NO AUDIT LINE, and that is a real distinction rather than a convenience: HA-5
 * forbids a forming bar reaching a PLAN — an entry, a stop, a size. This moves the far end of
 * a line already drawn dashed, already hollow-tipped, and already labelled `?` by
 * `openLegDrawing`'s plate, which keeps carrying the CLOSED number. So the two disagree on
 * purpose: the line shows where price is, the plate shows the last settled reading.
 *
 * The side comes from `liveKind`, never from comparing the new price to the old one: the
 * running extreme can EQUAL the pivot that opened the leg (nothing has traded past it yet),
 * where a comparison picks the wrong side and draws a level no bar printed.
 */
export function zigzagLiveTip(
  z: Zigzag,
  bars: readonly { time: number; high: number; low: number }[],
): ZigzagVertex | null {
  if (z.liveKind === null || z.points.length === 0) return null;
  const tail = z.points[z.points.length - 1];
  // The leg's own window: bars at or after the closed extreme. Earlier bars belong to the
  // legs already drawn, so letting one of them win would move the tail BACKWARDS in time.
  let best = tail;
  for (const b of bars) {
    if (b.time < (tail.time as number)) continue;
    const p = z.liveKind === 'high' ? b.high : b.low;
    if (z.liveKind === 'high' ? p > best.price : p < best.price) {
      best = { time: b.time as UTCTimestamp, price: p, major: false, pending: true };
    }
  }
  return best === tail ? null : best;
}

/**
 * STRUCTURAL DOTS — Andrei's own layout marks every pivot with a coloured dot, and that
 * dot IS how he reads structure at a glance: maroon on the major extremes, green on the
 * minor ones. When this was written the arrows carried SIDE (red high / teal low) and
 * confirmation state, so the one thing they did NOT show at a glance was DEGREE —
 * yellow-vs-red was a hue difference inside a palette that already spent hue on side.
 * The dot spends SIZE and one flat colour on degree alone, which is why it reads without
 * being decoded. (Those arrows were retired 2026-08-23, t_13260999; see the ZIGZAG
 * DECISION block, which also records that THIS function is dead.)
 *
 * MARKERS, NOT A PRIMITIVE, and deliberately: `setMarkers` already anchors a shape to a
 * bar and stacks coincident marks outward, which is the entire job here. A primitive
 * would mean re-deriving `timeToCoordinate` per dot plus a collision pass — the exact
 * code path that shipped broken three times on the break labels.
 *
 * NO TEXT — because a dot's whole point is being read without decoding, and a word in the
 * same slot is the "BOSSH" collision that made the degree-1 labels conditional in the first
 * place. The original justification was "the arrow beside it already says SH/SL/MH/ML",
 * which stopped being true when that text was retired; nothing on the pivots carries text
 * now, so if this function were ever revived the no-text rule rests on the collision alone.
 *
 * Rides the SWINGS checkbox — no new toggle. The panes are dense enough, and a dot is a
 * second reading of the same pivots the swings overlay is already about.
 */
export const DOT_MAJOR_COLOR = '#8b1a1a';
export const DOT_MINOR_COLOR = '#4caf50';
/** Not yet knowable (inside the trailing confirmation window), at either degree. */
export const DOT_UNCONFIRMED_COLOR = '#5a5d68';

export function structureDots(payload: SwingsPayload): SeriesMarker<Time>[] {
  const dots: SeriesMarker<Time>[] = payload.pivots.map((p) => {
    const isMajor = p.degree === 2;
    return {
      time: p.time,
      position: p.kind === 'high' ? ('aboveBar' as const) : ('belowBar' as const),
      shape: 'circle' as const,
      // Degree is the whole point of the dot, so an unconfirmed pivot keeps its DEGREE
      // size and loses only its colour — same honesty rule the arrows already use.
      color: !p.confirmed ? DOT_UNCONFIRMED_COLOR : isMajor ? DOT_MAJOR_COLOR : DOT_MINOR_COLOR,
      size: isMajor ? 1.4 : 0.6,
      text: '',
    };
  });
  // lightweight-charts silently DROPS out-of-order markers, and the pivot list is only
  // ascending by construction — sorting here costs nothing and removes the assumption.
  dots.sort((a, b) => (a.time as number) - (b.time as number));
  return dots;
}

/**
 * THE INDUCEMENT IS A LEVEL LINE, NOT A DOT. This function draws nothing, on purpose.
 *
 * Andrei, 2026-08-10, on the LTC 15m pane: "I still see these artifacts, i believe we
 * should not have them." MEASURED off that screenshot rather than described: 21 `IDM 15m`
 * text labels, each beside a small orange dot, spread across the pane -- one per SETUP,
 * because this builder emitted a marker for every row and `main.ts` appends them all on
 * the `setup` checkbox. His own reference carries FOUR.
 *
 * TWO DEFECTS, ONE OF FORM AND ONE OF COUNT, and `docs/reports/structure-spec-2026-08-10.md`
 * (branch `origin/spec/structure-video`) names both:
 *
 *   * FORM. Its section 2 legend table: "IDM level | black, thin | short horizontal
 *     segment | at the inducement price | `IDM` at the left end", verified on three of his
 *     frames. A dot with text beside it is the wrong primitive twice over -- the same
 *     section records that the DOTS in his frames are the structure points and "carry NO
 *     text at all" (`text: ''`, our `structurePointDots`). So an orange dot with a word on
 *     it competes visually with the one mark whose whole job is to be a wordless dot.
 *   * COUNT. Same section counts the inducement on his frames at x4 / x2 / x2-3 -- four on
 *     a screen, not twenty-one. A spent setup's inducement has already been taken; it is
 *     history, not a level to watch.
 *
 * BOTH ARE FIXED IN `setupBands`, which is the honest place for it: the inducement is a
 * PRICE LEVEL, and this file already has a level-with-a-label primitive that anchors its
 * plate at the price it names. See `idmLevels`. A marker cannot be that shape --
 * lightweight-charts draws markers at a BAR with text offset above or below it, which is
 * precisely how the label came to sit at a price it was not drawn at.
 *
 * WHY THE EXPORT SURVIVES AS AN EMPTY LIST rather than being deleted: `main.ts:2281`
 * appends its result to the marker bucket, and that file belongs to another change in
 * flight. Returning `[]` removes every dot and every `IDM <tf>` label from the pane with
 * no edit there; the call is dead weight for one commit and stream 1 can drop it.
 *
 * ponytail: a no-op kept for one caller. Delete the function and the `main.ts` call
 * together once both changes have landed.
 */
export function idmDots(_payload: SetupPayload): SeriesMarker<Time>[] {
  return [];
}

/** Human-readable summary for the sidebar. Counts come from the API, not from here. */
export function swingsSummary(p: SwingsPayload): [string, string][] {
  const rows: [string, string][] = [
    ['source', p.source],
    ['pivots', String(p.counts.total)],
    ['highs / lows', `${p.counts.highs} / ${p.counts.lows}`],
    ['% of bars', `${p.counts.pct_of_bars}%`],
  ];
  // THE OPEN LEG, replacing a dead `unconfirmed 0`. The old row could never be
  // non-zero (0 of 18 cached series), so it read as "nothing is pending" while the
  // newest 1-25 bars carried no label at all. This row states the leg in progress
  // and names its running extreme, which is the thing that was missing from the
  // chart entirely.
  if (p.open_leg) {
    const ol = p.open_leg;
    rows.push([
      'open leg',
      `${ol.bars} bar(s), running ${ol.kind} ${priceText(ol.price)} — provisional`,
    ]);
  } else {
    rows.push(['open leg', 'none — last pivot is the last bar']);
  }
  // The two guarantees of the pointer algorithm, shown as MEASURED values rather
  // than claimed in a tooltip. Both must read 0; a nonzero here is a real defect,
  // and the doubled-bar case was invisible until it was checked by bar index.
  rows.push([
    'alternation viol.',
    p.counts.alternation_violations === 0
      ? '0 — strictly alternating'
      : `${p.counts.alternation_violations} — same-kind neighbours`,
  ]);
  rows.push([
    'bars both H+L',
    p.counts.bars_both_high_and_low === 0
      ? '0'
      : `${p.counts.bars_both_high_and_low} — DEFECT`,
  ]);
  if (p.counts.confirm_lag_max !== null) {
    // The lag is VARIABLE here (a fractal width would have made it constant), so
    // "when did this become knowable" differs per swing and stays visible.
    rows.push(['confirm lag', `median ${p.counts.confirm_lag_median} / max ${p.counts.confirm_lag_max} bars`]);
  }
  // Degree 2. The density figure is the PAIRED REDUCTION gate made visible: an
  // earlier candidate passed every correctness gate precisely because it returned
  // degree-1 unchanged, so a number near 100% here means the hierarchy is a no-op.
  // The other end matters too — the suffix-extrema rule this replaced read 0.8% on
  // this chart, which is structurally correct and useless to look at.
  //   NO COLOUR IN THE KEY. It read `majors (yellow)` until 2026-08-23 (t_13260999),
  //   when the pivots stopped being yellow: degree moved to the zigzag's vertex radius,
  //   so on a default load ZERO of the marks this row counts are yellow (measured:
  //   `show-confirm` is off by default, so `swingMarkers` emits nothing at all). Naming
  //   the radius instead was rejected — the row is a COUNT, and the chart channel it
  //   would point at is measured non-separating at 15m density (see the ZIGZAG DECISION
  //   block's D2 paragraph), so the key names the thing counted and nothing else.
  rows.push([
    'majors',
    `${p.counts.majors} = ${p.counts.majors_pct_of_swings}% ` +
      `(${p.counts.major_highs}H / ${p.counts.major_lows}L)`,
  ]);
  if (p.counts.major_alternation_violations !== 0) {
    rows.push(['major alt. viol.', `${p.counts.major_alternation_violations} — DEFECT`]);
  }
  if (p.counts.majors_pending > 0) {
    // ONLY the right edge is provisional now, and it is stated as a count rather than
    // as a blanket "everything may move" caveat — the break-of-structure rule does not
    // repaint (0 of 1,243 streamed prefixes), so claiming otherwise would understate it.
    const sup = p.counts.majors_pending_superseded ?? 0;
    rows.push([
      'majors pending',
      `${p.counts.majors_pending} at the right edge — awaiting a break`
        // OUTRUN, said plainly. `ML?`/`MH?` is documented as "the open phase's running
        // extreme" and on 31.6% of cached series the open leg has already priced through
        // it. `ict.mss` is correct (a major must be a degree-1 swing) — what was wrong
        // was the claim, so the payload measures it and this row states it.
        + (sup > 0 ? `, ${sup} already outrun by the open leg` : ''),
    ]);
  }
  return rows;
}

// ---------------------------------------------------------------- structure breaks
export type BreakKind = 'BOS' | 'CHoCH';

export type BreaksPayload = {
  component: 'breaks';
  source: string;
  params: Record<string, never>;
  counts: {
    total: number;
    bos: number;
    choch: number;
    bullish: number;
    bearish: number;
    anchors: number;
    pct_of_swings: number;
  };
  breaks: {
    kind: BreakKind;
    idx: number; time: UTCTimestamp; direction: 'bullish' | 'bearish';
    level: number; level_idx: number; level_time: UTCTimestamp; price: number;
    /** CHoCH only — null on a BOS, which promotes no major. */
    anchor_idx: number | null;
    anchor_price: number | null;
    anchor_time: UTCTimestamp | null;
    confirm_idx: number; confirm_time: UTCTimestamp | null; confirmed: boolean;
  }[];
};

/**
 * Break colours. Teal-green BOS / brick-red CHoCH, per Andrei's reference drawing.
 *
 * NOT the candle palette: there, green/red mean up/down, and a break's colour here means
 * CONTINUATION vs CHARACTER CHANGE — a bearish BOS is still green. Direction is carried
 * by the arrow marker, so the two channels stay independent instead of one overloading
 * the other. Distinct from the major yellow, which is a third thing again (degree).
 */
export const BOS_COLOR = '#2e8b6f';
export const CHOCH_COLOR = '#c0503a';

/** The colour for one kind, so the marker, the segment and the legend cannot drift. */
export const breakColor = (kind: BreakKind): string =>
  kind === 'CHoCH' ? CHOCH_COLOR : BOS_COLOR;

/**
 * Breaks as ARROWS on the piercing swing. The KIND is carried by colour, not by text.
 *
 * Andrei, 2026-07-27: *"You can keep the way how you point to MSS (CHoCH) and BOS
 * candles."* So the marker geometry is unchanged from the retired MSS overlay — arrow at
 * `time`, the bar the break is ABOUT. `confirm_time` is when it became knowable and is
 * carried in the payload for the summary rows: the same two-instant discipline as every
 * other overlay here.
 *
 * THE `text` FIELD IS DELIBERATELY EMPTY, and this was MEASURED rather than styled.
 * Setting it to 'BOS'/'CHoCH' is the obvious thing and it is what produced the "confetti"
 * a vision pass reported on the live 2,170-bar BTC/1D feed: **189 marker labels, 173 of
 * them overlapping (91.5%)**, fragments like `CHoCHoCH` and `CH oCH`, and some drawn at
 * NEGATIVE x. lightweight-charts' `setMarkers` has no collision handling whatsoever — it
 * stamps every string it is given, so at 0.41 px per bar the words pile up.
 *
 * That mattered more than it looks, because the first fix went into the wrong layer: I
 * added a collision test to `BreakPrimitive` and re-measured, and its own plates were
 * already clean (19 drawn, 0 overlapping) while the chart still looked identical. The
 * cause was here.
 *
 * Nothing is lost. Every break already carries a labelled dashed segment from
 * `breakSegments`, whose plates ARE collision-tested, so the word appears exactly once
 * per break and only where it fits. The arrow keeps direction (shape/position) and kind
 * (colour); the sidebar count rows carry the same two colours as the legend.
 */
export function breakMarkers(p: BreaksPayload): SeriesMarker<Time>[] {
  return p.breaks
    .map((s) => {
      const bull = s.direction === 'bullish';
      return {
        time: s.time,
        position: bull ? 'belowBar' : 'aboveBar',
        shape: bull ? 'arrowUp' : 'arrowDown',
        color: breakColor(s.kind),
        size: s.kind === 'CHoCH' ? 1 : 0.8,
        // See the note above: text here is uncollided by lightweight-charts. The
        // segment's plate is the one place the word is drawn.
        text: '',
      } as SeriesMarker<Time>;
    })
    .sort((a, b) => (a.time as number) - (b.time as number));
}

/**
 * The pierced levels as BOUNDED dashed segments — the reference drawing's grammar.
 *
 * One segment per break, from the bar that SET the level to the bar that BROKE it. This
 * replaces the retired overlay's price lines, which spanned the whole pane and therefore
 * had to be capped at the 8 most recent to keep the chart readable. A bounded segment
 * does not accumulate (median span 7 bars, ~30 breaks per 300 bars measured over the
 * cache), so EVERY break is now drawn — nothing is silently withheld.
 *
 * Pure translation: prices, both timestamps and the kind arrive from the API. Colour,
 * dash and weight are decided here because they are presentation.
 */
export function breakSegments(p: BreaksPayload): BreakSegment[] {
  return p.breaks.map((s) => ({
    price: s.level,
    fromTime: s.level_time,
    toTime: s.time,
    color: breakColor(s.kind),
    label: s.kind,
    emphasis: s.kind === 'CHoCH',
  }));
}

export function breaksSummary(p: BreaksPayload): [string, string][] {
  const c = p.counts;
  return [
    ['source', p.source],
    ['breaks', String(c.total)],
    // The distinction that IS the feature, so it leads. Colour-coded in the sidebar via
    // the `bos`/`choch` classes, which makes these rows the chart's legend.
    ['bos (with trend)', String(c.bos)],
    ['choch (against)', String(c.choch)],
    ['bullish / bearish', `${c.bullish} / ${c.bearish}`],
    ['% of swings', `${c.pct_of_swings}%`],
    // Every CHoCH promotes exactly one swing to a major; a BOS promotes none. This is
    // the cross-check between this overlay and the `majors` count row — they come from ONE
    // walk in ict/mss.py, so a mismatch here is a real defect. (It said "the yellow
    // majors" until 2026-08-23, t_13260999: the pivots are no longer yellow.)
    ['major anchors', `${c.anchors} (= CHoCH count)`],
  ];
}

// ---------------------------------------------------------------- fvg
export type FvgPayload = {
  source: string;
  params: {
    inversion_rule: string; candle_basis?: string;
    /** Length of the CLOSED prefix of the snapshot the gaps were computed over. A
     *  `filled_at_idx >= closed_count` is a full fill on the FORMING bar (the tail is
     *  read for fills only, never births or inversions), and `filled_time` is then that
     *  bar's open. `forming_times` declares the tail's open times, index-aligned from
     *  `closed_count`, so a reader can place such an index without a second feed call. */
    closed_count?: number; forming_fill_only?: boolean; forming_times?: UTCTimestamp[];
  };
  counts: { total: number; bull: number; bear: number; filled: number; inverted: number };
  gaps: {
    idx: number; time: UTCTimestamp; lo: number; hi: number; ce: number;
    is_bull: boolean; formed_idx: number; formed_time: UTCTimestamp | null;
    filled_at_idx: number | null; inverted_at_idx: number | null;
    /** The bar that filled / inverted the gap, as a TIME. Null = never happened.
     *  Served by the API so this file never converts a bar index to a timestamp. */
    filled_time: UTCTimestamp | null; inverted_time: UTCTimestamp | null;
    /** Inclusive wick boundary supplied by ict.fvg; never a close/inversion trigger. */
    fill_boundary?: { side: 'high' | 'low'; price: number };
  }[];
};

export type OrderBlockPayload = {
  /** Market-wide totals, present only when the response was windowed. See `orderBlockSummary`. */
  counts_all?: { total: number };
  source: string;
  /** EMPTY. The old shape carried `ob_reference`; Andrei's 2026-08-02 spec has no tunables — a block
   *  is one candle and the forward-shift bound is derived from the swing structure. */
  params: Record<string, never>;
  counts: { total: number; bull: number; bear: number; shifted: number; live: number; mitigated: number };
  blocks: {
    /** The block candle, AFTER any forward shift. */
    idx: number;
    /** The candidate the search started from. Differs from `idx` when Step 4 moved the block. */
    sweep_idx: number;
    time: UTCTimestamp;
    /** WICK extremes of that one candle — «зона = весь диапазон свечи [high[N], low[N]] (с тенями)». */
    lo: number; hi: number; mid: number;
    is_bull: boolean;
    /** Size of the imbalance that validated the block. Always > 0. */
    gap: number;
    /** The bar that completed the imbalance — the block is not knowable before it closes. */
    formed_idx: number; formed_time: UTCTimestamp | null;
    /** When price traded back INTO the zone, consuming it. Null means never — still actionable. */
    mitigated_idx: number | null; mitigated_time: UTCTimestamp | null;
    live: boolean;
  }[];
};

export type SetupPayload = {
  /** Market-wide totals, present only when the response was windowed. See `setupSummary`. */
  counts_all?: { total: number; live?: number };
  source: string;
  /** `spot` and `atr` from the SERVER, because ATR needs full history and the client holds only the
   *  drawn window. Both null when the series is too short for a Wilder ATR. */
  params: { spot: number | null; atr: number | null };
  counts: {
    total: number; bull: number; bear: number; live: number; spent: number;
    order_block: number; fvg: number;
  };
  setups: {
    /** The structure break that opened the setup. */
    break_idx: number | null; break_time: UTCTimestamp | null;
    /** The inducement the break left behind, and the price of it. Null legs are real:
     *  a setup can be found without a labelled IDM, and drawing a guessed level would be
     *  worse than drawing none. */
    idm_idx: number | null; idm_time: UTCTimestamp | null; idm_level: number | null;
    /** The bar that SWEPT the inducement — the event that validates the setup. */
    swept_idx: number | null; swept_time: UTCTimestamp | null;
    /** The entry zone. `zone_kind` names WHICH kind, because an order block and an FVG are
     *  different objects with the same rectangle and the reader must be able to tell. */
    zone_lo: number | null; zone_hi: number | null;
    /** 'OB' | 'FVG' -- `ict.setup.Setup.zone_kind`'s OWN spelling, not a re-coining. Declaring
     *  'order_block' | 'fvg' here made `tsc` reject the correct comparison, which is the type
     *  system doing its job: the union IS the contract, and getting it wrong mislabels silently. */
    zone_kind: 'OB' | 'FVG' | null;
    is_bull: boolean | null;
    /** Zone not yet traded through, so the entry is still available. */
    live: boolean | null;
    /** Where the box STARTS: the sweep, falling back to the break. Served, so this file
     *  never converts a bar index to a timestamp. */
    time: UTCTimestamp | null;
  }[];
  /** THE ONE INDUCEMENT IN FORCE ON THIS TIMEFRAME RIGHT NOW, or null when the current one has
   *  been swept and no new impulse has left one behind. Computed by the server
   *  (`chartapp/api/overlays.py::_live_idm`) from `ict.idm` + `ict.mss`, NOT derived from
   *  `setups` above -- every row there carries an ALREADY-SWEPT inducement by construction
   *  (`ict.setup.find_setups`: `if not idm.swept: continue`), which is why nine of them once
   *  drew nine `IDM` plates on one 15m pane.
   *
   *  OPTIONAL IN THE TYPE, deliberately: this key was added 2026-08-25 and a cached or replayed
   *  payload predating it must render as "no live IDM" rather than crash the layer. */
  live_idm?: {
    idx: number | null; time: UTCTimestamp | null; level: number | null;
    /** 'up' == the level is a LOW guarding an up impulse; 'down' == a high. `Inducement.leg`'s
     *  own spelling, not a re-coining. */
    leg: 'up' | 'down' | null;
    break_idx: number | null; break_time: UTCTimestamp | null; break_kind: string | null;
  } | null;
  /** WHY `live_idm` is null, when it is (card `t_0cdae4dc`): `'no_break'` (no knowable full-body
   *  main-structure break yet, so there is no impulse to induce for), `'ladder_swept'` (every
   *  impulse-side rung has been taken), `'too_few_bars'`. Null whenever `live_idm` answers, and
   *  absent on a payload predating the field -- `idmStatus` then says "none" with no reason
   *  rather than inventing one. Served by `chartapp/api/overlays.py::_live_idm_absence`. */
  live_idm_none?: string | null;
};

export type LiquidityPayload = {
  source: string;
  // `tol_price` / `atr` are null exactly when `insufficient_history` is present: the series is shorter
  // than the ATR that scales the tolerance (xyz:XYZ100 1M, 12 bars) -- a declared state, not a 503.
  params: { tol_atr: number; tol_price: number | null; min_touches: number; atr: number | null };
  insufficient_history?: string | null;
  pools: {
    side: string; price: number; lo: number; hi: number; n_touches: number;
    formed_idx: number; confirm_idx: number; confirm_time: number | null; confirmed: boolean;
  }[];
  sweeps: {
    idx: number; time: number; side: string; pool_price: number;
    wick_price: number; close_price: number; pool_confirm_idx: number;
  }[];
  counts: { pools: number; buy_pools: number; sell_pools: number; sweeps: number };
};

/** Everything the liquidity overlay draws, from ONE pool set.
 *
 *  Bands and markers are returned TOGETHER because the reported defect was them
 *  disagreeing: two functions over the same payload, one capped and one not, produced
 *  245 sweep arrows pointing at pools that were never drawn (44.3% of 553). Making them
 *  one return value is what makes that class of divergence unrepresentable rather than
 *  merely fixed — and it is why `hiddenPools`/`orphanSweeps` are reported from here
 *  instead of recomputed by the sidebar. */
export type LiquidityDrawing = {
  bands: ZoneBand[];
  markers: SeriesMarker<UTCTimestamp>[];
  /** Pools this function DECLINED to draw, and the reason it can be non-zero.
   *
   *  IT WAS AN IDENTITY UNTIL 2026-08-22 (R4-7). `drawn` was `[...p.pools].sort(...)` — a
   *  full copy — so `p.pools.length - drawn.length` was structurally 0 and the
   *  `pools hidden` row was unreachable on every input that exists or could exist. That is
   *  the `counts.unconfirmed: 0` class of false-clean signal this very file deletes on
   *  sight (see `swingsSummary`), sitting in the disclosure the module was rewritten to
   *  provide. It is now counted from a real refusal: a pool whose `confirm_time` is not a
   *  finite number has no bar to anchor its band to. */
  hiddenPools: number;
  /** Sweeps whose pool is not drawn. **0 is the acceptance number for this card.** */
  orphanSweeps: number;
};

/**
 * Liquidity pools as BANDS, and their sweep markers, from ONE pool set.
 *
 * Andrei, 2026-07-28, pointing at the sidebar checkbox on an `xyz:INTC 1D` screenshot:
 * *"'Liquidity pools / sweeps' do not work and show liquidity sweeps at least on INTC"*.
 *
 * THE DETECTOR WAS NEVER BROKEN — the draw was. Verified against the live route on the
 * exact series in the screenshot: `pools 10 (5 BSL / 5 SSL) sweeps 4`, byte-identical to
 * the numbers he photographed. Cache-wide (114 usable series of 117) `pools == 0` on only
 * 6 and `sweeps == 0` on 18, mostly short 1M series. What the canvas actually showed was
 * measured by instrumenting `fillText`: **4 `sweep` glyphs, 0 `BSL`/`SSL` glyphs, 0 of 10
 * pool lines carrying any label at all** — ten dashed lines in two colours, no text, on a
 * pane already holding `TP (full) 116` / `ENTRY long 88` / `LIQ 74.1604`. Unreadable is
 * indistinguishable from broken, so his read was right in the way that matters.
 *
 * Three defects, all fixed here rather than in `ict/liquidity.py` (which is correct):
 *
 *  **D1 — the 8-line cap hid 37.0% of pools and orphaned 44.3% of sweep markers.**
 *  `liquidityLines` ranked by touch count and took 8; `liquidityMarkers` had no cap and
 *  mapped every sweep. So 245 of 553 markers cache-wide (44.3%, on 60 series) pointed at
 *  a pool whose line was not drawn — an arrow at nothing. The cap is gone, and the two
 *  outputs now come from ONE function over ONE pool set, so they cannot diverge again:
 *  that is the structural half of the fix, since a future cap would otherwise reopen it.
 *  Ink measured BEFORE removing it, the same way the FVG card did: union coverage of the
 *  pane is a median 9.7% (p10 0.7%, p90 30.4%, max 51.0%) and maximum overlap depth is 2
 *  on 113 of 114 series. A cap would now hide more than it saves.
 *
 *  **D2 — a pool is a ZONE and the payload already carried its edges.** The old draw
 *  collapsed each pool to the scalar `price`, so a 0.14%-wide shelf and an 18%-wide one
 *  were the same one-pixel line: measured over 1,110 pools, span as a % of pool price is
 *  p10 0.140% / median 0.970% / p90 18.266% / max 761.362%, and 33.5% of pools are WIDER
 *  than `tol` (the LINK_CHAIN transitivity `ict/liquidity.py` documents). `lo`/`hi` were
 *  served all along. Exactly the FVG defect, one overlay over.
 *
 *  **D3 — full-pane `createPriceLine`, which cannot carry a label.** Its `title` renders
 *  only as a price-axis label, and that is switched off here because a dozen stacked axis
 *  boxes covered the most recent ~40 bars. Hence 0 text glyphs. Bands ride the SAME
 *  `RangePrimitive` as the OTE zone and the FVG boxes — a third primitive with its own
 *  coordinate math is how the break-label pass shipped broken three times — which also
 *  inherits its measured guards for free: the 1-device-pixel floor (5 of 1,110 pools are
 *  zero-width, so their band renders as the hairline a ray would have drawn, with no
 *  second code path), the off-pane refusal, and collision-tested plates.
 *
 * THE ANCHOR IS `confirm_time`, NOT `formed_idx`, and the difference is causality. A pool
 * is not INFORMATION until its last constituent pivot confirmed — that is the whole reason
 * `detect_sweeps` runs with `gate=GATE_CONFIRM` — so a band starting at `formed_idx` would
 * claim the level was actionable before it could be known. Median gap is 1 bar but the max
 * is 33. It is also the only one of the two served as a TIME: this file converts no bar
 * index to a timestamp, ever.
 *
 * NOT USED, deliberately: the payload's `confirmed` flag. It is `false` on **0 of 1,110**
 * pools cache-wide, i.e. structurally always true — the `unconfirmed: 0` class of
 * false-clean signal this app deletes on sight, so no branch keys off it.
 */
export function liquidityDrawing(p: LiquidityPayload): LiquidityDrawing {
  // Most-touched first: the ORDER still matters even with no cap, because plate collision
  // is resolved first-come (see `RangePrimitive._label`), so the shelf with the most
  // touches should be the one that keeps its text when two pools sit a pixel apart.
  const ordered = [...p.pools].sort((a, b) => b.n_touches - a.n_touches);
  // A BAND NEEDS A BAR TO START AT, and `confirm_time` is declared `number | null`
  // (`api/overlays.py:1148` sends None when `confirm_idx > last`). The old code was
  // `fromTime: q.confirm_time as UTCTimestamp` — an `as` with no runtime guard, so a null
  // would reach `timeToCoordinate` and the band would silently not be drawn while the
  // sidebar still said `drawn M of M` (R4-7b). It is unreachable on today's cache (my own
  // measurement: 26 series, 840 pools, 0 nulls — `cluster_pools` takes
  // `confirm_idx = max(confirms)` over pivots already inside the bars), which is exactly
  // why it needed a real refusal rather than a cast: the next pool source with an
  // unclosed confirm — another feed, a `first_time` cut, a future detector — would
  // otherwise be invisible. Refused HERE, counted in `hiddenPools`, disclosed by the row.
  const drawn = ordered.filter((q) => Number.isFinite(q.confirm_time));
  const bands: ZoneBand[] = drawn.map((q) => {
    const buy = q.side === 'buy';
    const tone = buy ? LIQ_FILL.buy : LIQ_FILL.sell;
    return {
      lo: q.lo,
      hi: q.hi,
      fromTime: q.confirm_time as UTCTimestamp,
      // A pool is a price that keeps applying, so it runs to the right edge. It is NOT
      // bounded at its sweep: whether a swept pool has stopped existing is an ICT rule,
      // and this file does not decide ICT rules (`ict/liquidity.py` makes no such claim
      // either — `Pool` is frozen and carries no `swept` field). The sweep is reported as
      // its own marker, which is the payload's own shape.
      toTime: null,
      fill: tone.fill,
      border: tone.border,
      label: `${buy ? 'BSL' : 'SSL'} x${q.n_touches} ${priceText(q.price, q.lo, q.hi)}`,
    };
  });

  // Markers from the SAME set, matched on the price the API itself put on both objects.
  // Driving them from `p.sweeps` independently is what produced the 44.3% orphan rate.
  const drawnPrices = new Set(drawn.map((q) => q.price));
  const kept = p.sweeps.filter((s) => drawnPrices.has(s.pool_price));
  const markers: SeriesMarker<UTCTimestamp>[] = kept.map((s) => ({
    time: s.time as UTCTimestamp,
    // A buy-side sweep spikes UP through the highs, so its marker belongs above.
    position: s.side === 'buy' ? 'aboveBar' : 'belowBar',
    color: s.side === 'buy' ? '#f0b429' : '#7f5af0',
    shape: s.side === 'buy' ? 'arrowDown' : 'arrowUp',
    text: 'sweep',
  }));

  return {
    bands,
    markers,
    hiddenPools: p.pools.length - drawn.length,
    orphanSweeps: p.sweeps.length - kept.length,
  };
}

/** The liquidity palette. Hues are the retired price lines' so nothing shifts colour.
 *
 *  ALPHA MATCHES THE FVG BOXES' 0.30 for a measured reason, not by copying: the FVG work
 *  established on this very canvas that 0.13 over a small region composites to a delta of
 *  28 against the background and cannot be seen (two independent vision passes reported
 *  "no shaded fill" while 38 boxes sat in the draw list), while 0.30 gives 132-143. A pool
 *  band is SMALLER than an FVG zone — median 0.970% of price against the FVG's 2.85% — so
 *  it needs at least as much, never less. Compounding is safe on the same basis: maximum
 *  overlap depth is 2 on 113 of 114 cached series, so the worst realistic stack is
 *  1-(1-0.30)² = 0.51 and candles stay readable through it.
 *
 *  ONE series is an exception and it is disclosed rather than tuned away: HYPEBTC 1D has
 *  16 coincident sub-pixel pools (depth 16), which saturates. Lowering the alpha for every
 *  other series to flatter that one would make 113 series invisible to hide a smear on one
 *  — and the smear is TRUE: there really are 16 pools at that price. */
const LIQ_FILL = {
  buy: { fill: 'rgba(240, 180, 41, 0.30)', border: 'rgba(240, 180, 41, 0.85)' },
  sell: { fill: 'rgba(127, 90, 240, 0.30)', border: 'rgba(127, 90, 240, 0.85)' },
} as const;

/**
 * The sidebar rows, counted from WHAT WAS RENDERED — never from a second build (R4-6, high).
 *
 * `rendered` IS THE LOAD-BEARING ARGUMENT. This function used to call `liquidityDrawing(p)`
 * and report `d.markers.length`, which is the count BEFORE `ChartPane.redrawMarkers` applies
 * `insideWindow` — and that filter drops every marker whose bar this pane never loaded.
 * Measured on the previous pass: bars 1000–2000 with sweeps at t=100/1500/99999 gave
 * `builder markers = 3, orphanSweeps = 0`, the sidebar said `sweeps 3 of 3 marked`, and
 * exactly ONE marker reached the series. So the single number a reader uses to check that
 * nothing is hidden was computed on the wrong side of the filter — the very failure the
 * module was rewritten for (44.3% of sweep arrows pointing at pools that were never drawn),
 * recreated one step earlier.
 *
 * IT CARRIES THE `drawing` AS WELL AS THE SERIES' LIST, and that is not redundancy — it is
 * what makes the intersection an IDENTITY test. Recomputing the drawing here produces fresh
 * marker objects, so membership against them is false for every marker the pane actually
 * holds (measured while writing this: `1` became `0`), and falling back to matching on TIME
 * would double-count the two INTC sweeps that share `t=1783987200`. Reporting every number
 * from the one drawing the caller drew also removes the second build entirely: the row and
 * the canvas cannot come from two computations if there is only one.
 *
 * `undefined` means nothing has been drawn yet — a load sets markers before bars — and the
 * builder's own count is the honest answer for that moment. It is the ONLY way to reach the
 * unfiltered number, so a caller that has drawn cannot accidentally report it.
 */
export function liquiditySummary(
  p: LiquidityPayload,
  rendered?: { drawing: LiquidityDrawing; onSeries: readonly SeriesMarker<Time>[] },
): [string, string][] {
  // The route's declared state, printed as itself -- `sessionsSummary`'s `unsupported_tf` rule. Before
  // card t_66e6f2c0 this was a 503 and the pane said "request failed", as for a broken route.
  if (p.insufficient_history) return [['liquidity', p.insufficient_history]];
  const c = p.counts;
  // ONE drawing: the caller's if it drew, otherwise ours. The old row said
  // `drawn 8 most-touched`, which was honest about the rule and silent about the 2 pools it
  // was dropping on that very series. `drawn N of M` cannot be silent.
  const d = rendered?.drawing ?? liquidityDrawing(p);
  // `tol` is a price DISTANCE, so its readable precision is the INSTRUMENT's, not its
  // own: it arrives as `tol_atr x atr` and carries ~15 IEEE-754 decimals, which
  // `priceText` with no basis caps at 8 and prints as `2.04667857`. Passing the pool
  // edges as the basis quotes it the way the market quotes itself (`2.047` on INTC,
  // eight places on a BTC ratio pair) — the same "derive the precision, never hardcode
  // it" rule, applied to the right inputs. Plain `toFixed(2)` was the previous shape and
  // is wrong at both ends of this app's universe.
  const scale = p.pools.flatMap((q) => [q.lo, q.hi]);
  // THIS overlay's markers among everything the series was given: the pane merges four
  // sources into one `setMarkers` call, so the count has to be of the sweeps in that list
  // and not of its length.
  const built = new Set<unknown>(d.markers);
  const marked = rendered === undefined
    ? d.markers.length
    : rendered.onSeries.filter((m) => built.has(m)).length;
  const rows: [string, string][] = [
    ['source', p.source],
    ['tol', `${p.params.tol_atr}xATR = ${priceText(p.params.tol_price ?? NaN, ...scale)}`],
    ['min_touches', String(p.params.min_touches)],
    ['pools', `${c.pools} (${c.buy_pools} BSL / ${c.sell_pools} SSL)`],
    ['drawn', `${d.bands.length} of ${c.pools} as zones (lo–hi)`],
    ['sweeps', `${marked} of ${c.sweeps} marked`],
  ];
  // Both are 0 today and both are reported anyway if they ever are not — a marker
  // pointing at an undrawn pool was the reported defect, so it must never be able to
  // happen quietly again.
  if (d.hiddenPools > 0) {
    rows.push(['pools hidden', `${d.hiddenPools} — no confirmed bar to anchor the band to`]);
  }
  if (d.orphanSweeps > 0) {
    rows.push(['sweeps unmarked', `${d.orphanSweeps} — their pool is not drawn`]);
  }
  // THE THIRD STATE, and it is the one R4-6 was about: a sweep the builder MARKED whose
  // bar is outside the window this pane loaded. Not an orphan (its pool is drawn) and not
  // hidden (nothing refused it) — it is simply off this pane, and saying so is what stops
  // `sweeps 1 of 3` reading as a detector that found one.
  const offWindow = rendered === undefined ? 0 : d.markers.length - marked;
  if (offWindow > 0) {
    rows.push(['sweeps off window', `${offWindow} — outside the bars this pane loaded`]);
  }
  return rows;
}

/** Project live wick touches onto already-confirmed gaps. No births or inversions.
 * Scan the pane's retained bars, not just its latest close: retraces and rollover
 * cannot resurrect a spent gap while a closed-bar detector refresh is in flight.
 * The server supplies the touch boundary; the frontend does not derive an ICT rule.
 */
export function liveFvgPayload(p: FvgPayload, bars: readonly { time: number; high: number; low: number }[]): FvgPayload {
  let newlyFilled = 0;
  const gaps = p.gaps.map(g => {
    const boundary = g.fill_boundary;
    if (!unfilled(g) || !boundary || g.formed_time == null) return g;
    const hit = bars.find(b => b.time > g.formed_time! && (boundary.side === 'low'
      ? b.low <= boundary.price : b.high >= boundary.price));
    if (!hit) return g;
    newlyFilled += 1;
    // A display projection has no detector index. Keep confirmed fields intact;
    // filled_time is sufficient to exclude the gap from the active draw list.
    return { ...g, filled_time: hit.time as UTCTimestamp };
  });
  return { ...p, gaps, counts: { ...p.counts, filled: p.counts.filled + newlyFilled } };
}

function unfilled(g: FvgPayload['gaps'][number]): boolean {
  return (g.filled_at_idx ?? -1) < 0 && g.filled_time == null;
}

/** Select geometric neighbours, not one bullish and one bearish gap. */
function nearestFvgs(p: FvgPayload, price: number | null | undefined): FvgPayload['gaps'] {
  if (price == null || !Number.isFinite(price)) return [];
  type Gap = FvgPayload['gaps'][number];
  let above: Gap | undefined;
  let below: Gap | undefined;
  for (const g of p.gaps) {
    if (!unfilled(g) || !Number.isFinite(g.lo)
      || !Number.isFinite(g.hi) || g.lo >= g.hi) continue;
    if (g.lo >= price && (!above || g.lo < above.lo
      || (g.lo === above.lo && g.time > above.time))) above = g;
    if (g.hi <= price && (!below || g.hi > below.hi
      || (g.hi === below.hi && g.time > below.time))) below = g;
  }
  return [above, below].filter((g): g is Gap => g !== undefined);
}

/**
 * FVG zones as RECTANGLES — the reference geometry, not a single CE line.
 *
 * Andrei, 2026-07-28, with a diagram ("Long Setup Geometry — Sweep → MSS → FVG"):
 * an FVG is a semi-transparent box from candle-1's high to candle-3's low, projected
 * forward in time, with a dashed CE bisector as the entry. The app drew only the
 * bisector, as a full-pane `createPriceLine`, and threw the rest of the payload away.
 *
 * WHY THE BOX AND NOT THE LINE, measured rather than argued: on the live BTC 1D feed
 * a 4,084-wide zone (84709→88793) and a 46-point zone (78562→78608) were the SAME
 * one-pixel line. Cache-wide (117 series, 1,327 live gaps) the box height spans a
 * median 2.85% of CE with p10 0.38% and max 107.99% — a 280x spread the CE line
 * renders identically. The height IS the risk in the reference diagram.
 *
 * Display policy (2026-09-20): only the nearest unfilled gap above and below the
 * pane's latest price. Distance is to the near edge, independent of BISI/SIBI.
 * A price inside a gap excludes it; touching an edge qualifies. Equal distances
 * prefer the newest gap. Missing/invalid price draws nothing. The full detector
 * payload stays intact; live wick consumption is projected before selection.
 *
 * THE RIGHT BOUND, and the card premise that measurement refuted. The ticket asked to
 * bound each box at its FILL bar. That bound is structurally unreachable here: a filled
 * gap is excluded from the draw (the reference shows live zones only, and the ticket
 * itself prohibits a show-filled toggle), and `filled_time` is non-null only for filled
 * gaps — measured 0 of 1,327 drawable gaps under BOTH rules. Binding it to `filled_time`
 * would have been dead code that no test could distinguish from working code, which is
 * the `unconfirmed: 0` class of false signal this app deletes on sight.
 *
 * The bound that IS real is the INVERSION. Under `ce_close` a gap can flip polarity
 * WITHOUT being filled (measured: 36 gaps cache-wide; 0 under `traded_through`, where
 * inversion is definitionally the fill). Such a gap is two different arrays over time:
 * a virgin FVG from candle-1 until the flip, and an INVERTED gap — opposite function,
 * ICT's IFVG — from the flip onwards. So it is drawn as two bounded phases sharing that
 * boundary, rather than one box that averages two contradictory claims. Every phase
 * boundary comes from the API's own timestamps; this file converts no index to a time.
 *
 * WHY FILLED GAPS ARE EXCLUDED. The reference draws live zones only, and under the
 * default rule an inverted gap IS a filled one (117/117 series), so there is nothing
 * left to draw for it that the fill has not already ended.
 */
export function fvgBands(p: FvgPayload, price: number | null | undefined,
  bars: readonly { time: number; high: number; low: number }[] = []): ZoneBand[] {
  p = liveFvgPayload(p, bars);
  const out: ZoneBand[] = [];
  for (const g of nearestFvgs(p, price)) {
    const tone = g.is_bull ? FVG_FILL.bull : FVG_FILL.bear;
    const kind = g.is_bull ? 'BISI' : 'SIBI';
    // CE is served unrounded on purpose (it is a midpoint others compute with), so the
    // rounding happens at this display edge — see `priceText`. Its precision is derived
    // from the zone's own edges, which is why they are passed as the basis.
    const zone = `${priceText(g.lo)}–${priceText(g.hi)} · CE ${priceText(g.ce, g.lo, g.hi)}`;
    // Only reachable under `ce_close` (see above): unfilled yet already flipped.
    const flip = g.inverted_time ?? null;
    if (flip != null) {
      // Phase 1 — the virgin gap, BOUNDED at the flip. Drawn at all because the box is
      // where price came from; it ends because what it described is over.
      out.push({
        lo: g.lo, hi: g.hi, fromTime: g.time, toTime: flip,
        fill: tone.fill, border: tone.border, midline: g.ce,
        label: `${kind} ${zone}`,
      });
      // Phase 2 — the INVERTED array, from the flip rightwards. Its function is the
      // opposite one, so it takes the opposite direction's colour rather than being a
      // second box in the same hue.
      const inv = g.is_bull ? FVG_FILL.bear : FVG_FILL.bull;
      out.push({
        lo: g.lo, hi: g.hi, fromTime: flip, toTime: null,
        fill: inv.fill, border: inv.border, midline: g.ce,
        label: `${kind} inverted ${zone}`,
      });
      continue;
    }
    out.push({
      lo: g.lo,
      hi: g.hi,
      fromTime: g.time,
      // Live and never flipped: runs to the right edge, which is correct for it.
      toTime: null,
      fill: tone.fill,
      border: tone.border,
      midline: g.ce,
      label: `${kind} ${zone}`,
    });
  }
  return out;
}

/**
 * A price as text, with the precision its own inputs justify — no more, no less.
 *
 * FOUND ON THE LIVE CHART, not in a test: a zone label read
 * `BISI 16872.8–16900.1 · CE 16886.449999999997`. The CE is a midpoint, so IEEE-754
 * binary division leaks its own representation error into the UI. The API is right to
 * serve the value UNROUNDED — `ict/fvg.py` is explicit that CE is "the 50% midpoint of
 * the zone, UNROUNDED", and rounding at the source would corrupt the arithmetic every
 * downstream consumer does. So it is rounded HERE, at the display edge, which is the
 * same "round at the edge, never in the primitive" rule `ict/fvg.py` states for `_rp`.
 *
 * The decimal count is DERIVED, not a knob: one more place than the noisiest input
 * needs, because halving two 1-decimal prices genuinely produces a 2-decimal midpoint.
 * A hardcoded `toFixed(2)` would be wrong in both directions across this app's
 * universe — BTC trades near 100,000 and ETHBTC near 0.03.
 */
export function priceText(v: number, ...basis: number[]): string {
  const decimals = (x: number): number => {
    const s = String(x);
    const dot = s.indexOf('.');
    // Exponential notation (tiny ratio prices) carries no readable decimal count;
    // fall back to the value's own string, which is already minimal.
    if (s.includes('e') || s.includes('E')) return -1;
    return dot < 0 ? 0 : s.length - dot - 1;
  };
  const base = basis.length ? basis : [v];
  const want = Math.max(...base.map(decimals));
  if (want < 0) return String(v);
  // +1 place: the midpoint of two N-decimal prices needs N+1 to be exact.
  const places = Math.min(8, want + 1);
  return String(Number(v.toFixed(places)));
}

/** The FVG palette. Direction colours are the retired CE lines' so nothing shifts hue.
 *
 *  THE ALPHA IS NOT THE OTE'S, and that is measured rather than taste. Reusing the OTE
 *  band's `0.13` looked right by precedent and was invisible in practice: sampling the
 *  composited canvas gave `rgb(47,31,39)` inside a red zone against a `rgb(19,23,34)`
 *  background — a 28/8/5 delta. Two independent vision passes over the live chart
 *  reported "no rectangles, no shaded fill" while the draw list held 38 boxes and 120
 *  border strokes, i.e. the geometry was correct and the paint was below the threshold
 *  of being seen. The mechanism is area, not preference: the OTE band spans 17% of a
 *  range's height, an FVG zone a median 2.85% of price, so the same alpha over a much
 *  smaller region reads as nothing.
 *
 *  `0.30` is safe against compounding for a measured reason, not a guessed one: maximum
 *  OVERLAP DEPTH across all 117 cached series is 2 (median 1), so the worst stack is
 *  1-(1-0.30)² = 0.51 — still translucent, candles still readable through it. */
const FVG_FILL = {
  bull: { fill: 'rgba(38, 166, 154, 0.30)', border: 'rgba(38, 166, 154, 0.85)' },
  bear: { fill: 'rgba(239, 83, 80, 0.30)', border: 'rgba(239, 83, 80, 0.85)' },
} as const;

/** Order-block zones. Reuses `ZoneBand` and `rangeprimitive`, so no new drawing code exists. */
export function orderBlockBands(p: OrderBlockPayload, showSpent = false): ZoneBand[] {
  const out: ZoneBand[] = [];
  for (const b of p.blocks) {
    // LIVE ONLY BY DEFAULT. Andrei, 2026-08-02: "hard to see anything and make trading decisions."
    // Measured cause, not a guess: of the 78 blocks in BTC/15m's drawn window, 66 had already been
    // traded through. A spent block is history — the order flow that created it is consumed — so
    // drawing it competes for attention with the levels that can still be traded.
    //
    // Kept behind a flag rather than dropped server-side: where price DID react is evidence when you
    // are checking the detector, and deleting it would make the overlay unauditable.
    if (!b.live && !showSpent) continue;
    const tone = b.is_bull ? FVG_FILL.bull : FVG_FILL.bear;
    // The imbalance is the evidence, so it leads the label. `↷` marks a block that Step 4 shifted
    // forward from its sweep, which is the first thing to check when a drawing looks misplaced.
    const shifted = b.idx !== b.sweep_idx ? ` ↷${b.idx - b.sweep_idx}` : '';
    out.push({
      lo: b.lo,
      hi: b.hi,
      // FROM the bar that completed the imbalance: the block is not knowable before then, and drawing
      // from its own candle would put the zone before its own cause.
      fromTime: b.formed_time ?? b.time,
      // A LIVE block projects to the right edge — it is a level price has not reached. A spent one
      // STOPS where price consumed it: drawing it onward would claim a level that no longer exists.
      toTime: b.live ? null : b.mitigated_time,
      fill: tone.fill,
      border: tone.border,
      midline: b.mid,
      label: `OB ${b.is_bull ? 'bull' : 'bear'}${shifted} gap ${priceText(b.gap)}`,
    });
  }
  return out;
}

export function orderBlockSummary(p: OrderBlockPayload): [string, string][] {
  const c = p.counts;
  return [
    ['source', p.source],
    // LIVE FIRST: the count of untested levels is the number a trader acts on. `total` follows so the
    // panel never hides how much of the detector's output is already spent history.
    ['live / total', `${c.live} / ${c.total}`],
    ...(p.counts_all && p.counts_all.total !== c.total
      ? ([['whole market', String(p.counts_all.total)]] as [string, string][])
      : []),
    ['spent', String(c.mitigated)],
    ['bull / bear', `${c.bull} / ${c.bear}`],
    // How many needed Step 4's forward shift — a reading aid, not a tunable.
    ['shifted forward', String(c.shifted)],
  ];
}

/** Setup zones — the ENTRY box of each complete setup, plus its IDM level.
 *
 *  Reuses `ZoneBand` and `rangeprimitive` exactly as `orderBlockBands` and `fvgBands` do, so
 *  this adds NO drawing code. A fourth zone primitive with its own coordinate math is how the
 *  break-label pass shipped broken three times.
 */
/** How far price must travel to REACH a zone, in ATR units. 0 when spot is inside the zone.
 *
 *  Andrei, 2026-08-04: SOL 1D drew three live setups at 7.53-8.47, 12.64-13.59 and 58.60-61.92 with
 *  spot at 73.40 -- the first two are 2022-era order blocks that price will not revisit. They are
 *  correctly live (never traded through) and worthless to trade. Measured across BTC/ETH/SOL/AAVE x
 *  1W/1D/15m, 114 live setups:
 *
 *      <=  5 xATR keeps 24%   SOL 15m EMPTIED
 *      <= 10 xATR keeps 36%   SOL 15m EMPTIED
 *      <= 20 xATR keeps 54%   nothing emptied  <- the tightest value that empties no timeframe
 *
 *  Hence a DEFAULT of 20 rather than a tighter one that would reproduce the same "I see nothing"
 *  report with a different cause. It is a fit to four assets, not a rule from the source, which is
 *  why it is a knob labelled uncalibrated and not a constant.
 */
export function zoneDistanceAtr(
  zoneLo: number,
  zoneHi: number,
  spot: number,
  atr: number,
): number | null {
  // null, not Infinity: a missing or zero ATR must not silently filter EVERY setup out. The caller
  // then keeps the setup, because refusing to draw on an unmeasurable input hides real entries.
  if (!Number.isFinite(atr) || atr <= 0 || !Number.isFinite(spot)) return null;
  // AN UNUSABLE EDGE IS `null`, NOT 0 (R4-5, 2026-08-21). `gap` below falls through both
  // comparisons when an edge is NaN — every comparison against NaN is false — and 0 is the
  // MOST reassuring answer this function has: it means "spot is INSIDE the zone", i.e. the
  // nearest, highest-priority category, and it survives the one filter (`d > maxDistanceAtr`)
  // that could have dropped the band. So a zone with an edge the detector never named read as
  // "enter now". Same family as `float(atr) <= 0` on the backend: a `<`/`===` test is never a
  // NaN test. Measured before the fix: `zoneDistanceAtr(NaN, 64500, 64000, 500)` = 0.
  if (!Number.isFinite(zoneLo) || !Number.isFinite(zoneHi)) return null;
  const gap = spot > zoneHi ? spot - zoneHi : spot < zoneLo ? zoneLo - spot : 0;
  return gap / atr;
}

/**
 * The inducement level's colour.
 *
 * NOT GREY ANY MORE (card `t_0cdae4dc`, 2026-10-11). Andrei: «почему я никогда не вижу IDM ни на
 * одном из графиков?» The level WAS on screen -- prod BTC/15m, ETH/1h and xyz:XYZ100/15m all held
 * exactly one IDM band agreeing with the server's `live_idm` (`process/reviews/t_0cdae4dc/shots/
 * prod_before_*.png`) -- but it was `#787b86`, the same grey as the axis text, the range `eq` edge
 * and the unconfirmed-pivot marker, i.e. the colour this chart uses for "background furniture".
 * The reference's black-on-white was a NEUTRAL line on HIS chart; on ours neutral reads as nothing.
 *
 * MAGENTA, hue 315 deg, because it is the widest gap in the hue circle this chart's own colour
 * literals leave: every saturated colour in `chartapp/web/src/*.ts` sits at 0-47 (reds, ambers),
 * 122-291 (green, teal 174, blues, violets, the off-by-default sessions' Frankfurt 291) or 340-348
 * (rose), so 315 is 23 deg from the nearest. Cyan was the first pick and was REFUSED by that
 * measurement: 12 deg from the setup/FVG teal `rgb(38,166,154)` and 1 deg from the session-levels
 * `Asia->RTH range` border. `idmvisible.test.ts` re-derives the gap from the source files, so a new
 * colour that crowds it fails a test rather than a reader.
 */
export const IDM_LEVEL_COLOR = '#ff3dd0';

export function setupBands(p: SetupPayload, showSpent = true, maxDistanceAtr = 20): ZoneBand[] {
  const out: ZoneBand[] = [];
  const { spot, atr } = p.params;
  for (const s of p.setups) {
    // SPENT SETUPS ARE DRAWN. Andrei, 2026-08-09: the transparent trading area should
    // ALWAYS display, and this filter is half of why it did not -- the default dropped
    // every setup whose zone price had already traded through, which on a 1D window is
    // most of them. An order block can afford the same filter because a spent block has a
    // MITIGATION bar to draw to; a spent setup has none (the API serves no such time), so
    // it collapses to a zero-width bound and the visible-width floor in `bandRect` is the
    // other half of the fix. The flag stays because the caller decides, not this function.
    if (s.live !== true && !showSpent) continue;
    // TOO FAR TO TRADE, LIVE OR SPENT. Andrei, 2026-08-10, on a screenshot of the 1D and 15m
    // panes with OB and FVG UNTICKED: "what are these red and green zones that are not
    // disappearing when I'm changing the scale of panes? Order blocks? If so they are defined
    // incorrectly."
    //
    // THEY ARE NOT ORDER BLOCKS AND THEY ARE NOT STALE -- both were measured. The fills are
    // `FVG_FILL.bull/bear` (teal/red 0.30), which on a pane with `liq` off is `setupBands` and
    // nothing else; liquidity draws amber/violet and `idmLevels` magenta (`IDM_LEVEL_COLOR`). And
    // nothing accumulates:
    // all four `attachPrimitive` sites are in the `ChartPane` constructor, every geometry local
    // in `refreshOverlaysOnce` starts at `[]` each run, and `range.setData` replaces wholesale.
    // A scale change does not clear them because every one of them is genuinely in the payload.
    //
    // The reach filter used to be LIVE-ONLY, on the reasoning that a spent zone is audit
    // evidence and "where price DID react matters regardless of distance". Measured, that
    // reasoning does not survive contact with his chart: 27 of 31 zones on the 1D pane were
    // spent, at a median 17.9xATR from spot and up to 38.6 -- history stacked into a brick wall
    // over the part of the chart he is actually trading. Audit evidence nobody can read is not
    // evidence. Applying the SAME reach to spent zones takes 1D from 38 bands to 26 and 15m from
    // 186 to 121.
    //
    // ONE RULE FOR BOTH rather than a second constant: the question "is this zone near enough to
    // matter?" has one answer, and `null` from `zoneDistanceAtr` still keeps the zone so an
    // unmeasurable ATR cannot silently empty the overlay.
    // A zone needs BOTH edges and a start time to be a rectangle. Skipped rather than
    // defaulted: an invented edge would draw an entry at a price the detector never named.
    //
    // FINITE, NOT MERELY NON-NULL (R4-5, 2026-08-21). `=== null` is not a NaN test, so a
    // `zone_lo: NaN` band used to reach the output as `{"lo": null, ...}` — a rectangle
    // with no bottom — and `zoneDistanceAtr` answered **0** for it, which is the reach
    // filter's most reassuring verdict ("spot is inside the zone", the nearest and
    // highest-priority category). Backend cause is card t_67271715 (the entry validator is
    // off the path); a fixed backend does not license the client to turn NaN into 0,
    // because 0 here means "enter now".
    //
    // THE ORDER OF THESE TWO BLOCKS IS NOT PART OF THE FIX, and that was measured rather
    // than assumed: the reach filter only ever `continue`s, so it cannot KEEP a band this
    // drop would remove, and the two are commutative. Enumerated over all four
    // combinations of {finite guard here} x {finite guard in `zoneDistanceAtr`}, the
    // drawn/not-drawn answer for a NaN edge is identical either way — the mutation that
    // swaps them survives the suite CORRECTLY. The drop is written first only because it
    // reads as the precondition it is. What makes the difference is the PREDICATE, in both
    // places: `zoneDistanceAtr` carries its own finite check because it is exported and
    // has other callers.
    if (
      s.zone_lo === null || !Number.isFinite(s.zone_lo)
      || s.zone_hi === null || !Number.isFinite(s.zone_hi)
      || s.time === null
    ) continue;
    if (maxDistanceAtr > 0) {
      const d = zoneDistanceAtr(s.zone_lo, s.zone_hi, spot ?? NaN, atr ?? NaN);
      if (d !== null && d > maxDistanceAtr) continue;
    }
    const tone = s.is_bull ? FVG_FILL.bull : FVG_FILL.bear;
    // `zone_kind` is IN THE LABEL because an order block and an FVG are different objects
    // behind an identical rectangle, and which one it is changes how the entry is taken.
    // `ict.setup` spells these 'OB' and 'FVG'. Comparing against lowercase 'fvg' would label
    // every FVG zone as an order block -- and the mislabel is invisible, because both draw the
    // same rectangle. The API-side version of this exact bug made two counters read zero.
    const kind = s.zone_kind === 'FVG' ? 'FVG' : 'OB';
    out.push({
      lo: s.zone_lo,
      hi: s.zone_hi,
      // FROM the sweep (the API already falls back to the break): the setup is not knowable
      // before the inducement is taken, so the box must not predate its own cause.
      fromTime: s.time,
      // LIVE projects to the right edge — the entry is still there. A SPENT one stops at its
      // zone, exactly as a mitigated block does, rather than claiming a level that is gone.
      toTime: s.live === true ? null : s.time,
      fill: tone.fill,
      border: tone.border,
      // THE PROXIMAL EDGE, NOT THE BISECTOR -- and the difference is money.
      //
      // `RangePrimitive` documents this dashed line as "the box is the zone, the midline is the
      // entry". For an FVG that is true: its consequent encroachment IS the 50% level, which is
      // why `:816`/`:825` pass `g.ce`. This line reused the same idea for SETUP zones, most of
      // which are ORDER BLOCKS and have no CE at all.
      //
      // MEASURED on 600 real BTC/15m bars at spot 64904, 288 setups:
      //     (lo+hi)/2 differs from `approach.entry_price` on 288 of 288 zones
      //     |delta| min 2.00 / median 65.00 / MAX 341.00 USD; live zones max 207.00
      // So the chart drew a dashed line captioned as the entry at a price the scan would never
      // enter at, beside a live trade panel -- and `approach.stop_target` derives the stop and
      // target from the EDGE, so every level downstream of that line was inconsistent with it.
      //
      // `approach.entry_price(zone_lo, zone_hi, spot)` is `zone_hi if spot > zone_hi else
      // zone_lo` -- the edge price reaches FIRST. Mirrored here rather than imported because the
      // browser cannot import the Lambda; the test asserts the two agree on both sides of the
      // zone, so a change to the convention breaks this line loudly instead of silently.
      // Null spot -> no line at all: an entry we cannot place is not a line we may draw.
      //
      // AND NEITHER IS AN ENTRY THAT IS GONE. A SPENT setup gets no entry line, which is the
      // density reduction Andrei's "overall layout looks overloaded" asked for, taken without
      // hiding anything: it is the SAME rule as the null-spot refusal one line above and the
      // same rule as the dropped caption below -- price has already traded through this zone,
      // so there is no entry left to place at its edge and a dashed line there claims one.
      // MEASURED on the same 5,015 BTC/15m bars: 284 drawn zones, 15 live, so
      //     before  2*284 edges + 284 midlines = 852 horizontal strokes
      //     after   2*284 edges +  15 midlines = 583   (-269, a 31.6% cut)
      // NO INFORMATION IS LOST. The RECTANGLE stays at its own price, height, side and colour
      // -- that is what a spent zone is FOR (seeing where price reacted) and it is what
      // 53ba07a made visible in the first place. Only the claim about a future entry goes.
      midline:
        s.live !== true || spot === null || !Number.isFinite(spot)
          ? null
          : spot > s.zone_hi
            ? s.zone_hi
            : s.zone_lo,
      // ONLY A LIVE SETUP IS CAPTIONED. Andrei, 2026-08-09: "overall layout looks overloaded.
      // Should be way cleaner." Measured from his screenshot: BTC/15m drew 34 of these strings
      // -- `setup short OB idm 64865`, `... 64266`, `... 64995` and ~30 more -- crossing each
      // other into a mat nothing could be read out of, while the summary said `live / total
      // 7 / 34`. So 27 of the 34 captions described entries that no longer exist.
      //
      // THE RECTANGLE STAYS. That is the whole point of the split, and it is deliberate rather
      // than a compromise: the spent zone being INVISIBLE was the defect fixed the same day
      // (`showSpent` defaulting on, plus the visible-width floor in `bandRect`), and re-hiding
      // it here would revert that. What a spent zone is FOR is seeing where price reacted, and
      // a box's price, height, side and colour say that with no text at all. Its caption said
      // nothing the box did not, 27 times over, on top of the 7 that mattered.
      //
      // The count is not hidden either: `setupSummary` states `live / total` and `spent`, so
      // "how many are there" is answerable from the panel rather than by counting captions.
      // An EMPTY label means "draw the box, print nothing" — see `ZoneBand`.
      //
      // NO INDUCEMENT PRICE IN THIS CAPTION, and that removal is the fix, not a trim.
      // The caption used to read `setup long OB idm 63785` while `RangePrimitive` anchors
      // every band plate at `(b.fromTime, b.hi)` -- the zone's TOP EDGE. So the text named
      // one price and the plate touched another.
      //
      // MEASURED on 5,015 real BTC/15m bars at spot 65065 (ATR 88.87), the 15 live captioned
      // zones the pane actually draws:
      //     |idm_level - zone_hi (the line the plate sits on)|: min 1 / median 87 / MAX 597 USD
      //     idm_level OUTSIDE its own box on 11 of 15 -- the text was not merely off the line
      //     it touched, it named a price the rectangle does not contain
      //     worst: `idm 65118` printed on a top edge at 65715 (zone 65428-65715)
      //
      // The caption's job is to identify THE BOX: direction, and which kind of zone it is,
      // because an order block and an FVG draw an identical rectangle and are entered
      // differently.
      //
      // *** WHAT THIS PARAGRAPH USED TO CLAIM, AND WHY IT NO LONGER HOLDS (card `t_ec068c65`,
      // 2026-08-25). *** It said "no information leaves the chart" because `idmDots` drew a dot
      // at `(idm_time, idm_level)` FROM THE SAME PAYLOAD ROW, so the number moving off this
      // caption was still on screen. That is FALSE now: `idmLevels` draws ONE level per pane --
      // the inducement in force, from `live_idm` -- so a row whose inducement has already been
      // swept has no mark anywhere, which is the whole point (Andrei: nine `IDM` plates on one
      // 15m pane). This row's OWN `idm_level` is therefore genuinely off the chart, and that is
      // a deliberate cut rather than a lossless move: a spent inducement is a price nothing is
      // waiting at. The full history remains in the payload and on the setups tab.
      //
      // WHY NOT ANCHOR THE PLATE AT `idm_level` INSTEAD: that would be honest too, but the
      // inducement is outside the zone on 11 of 15 live zones here, so the plate would detach
      // from the box it captions and the only cue tying text to rectangle (adjacency) would be
      // gone. A caption that claims no price cannot be wrong about one.
      //
      // SIBLING CAPTIONS WERE CHECKED AND ARE CLEAN, which is why the guard is here rather
      // than in `RangePrimitive`: FVG/inverted FVG print `lo-hi · CE` (measured: CE inside
      // [lo,hi] on 27 of 27 live gaps -- the plate's own edge is in the text), liquidity prints
      // the pool price (inside [lo,hi] on 76 of 76 pools), and an order block prints `gap`,
      // which is a WIDTH and not a level at all. Only the setup band named a foreign object's
      // price, so there is no shared defect for a shared guard to catch.
      //
      // IT LEADS WITH `wait`, NOT `setup`, AND CERTAINLY NOT `entry`. Andrei, 2026-08-12: *"Also,
      // I don't understand how to use them."* The word `setup` said only "the detector found
      // something"; every sibling string in this feature called the box an ENTRY (the overlay
      // checkbox, the summary row) -- and the plate is anchored at `b.hi`, the zone's TOP edge,
      // while the entry is the NEAR edge (`midline` above, `approach.entry_price`). On a LONG the
      // near edge is `zone_lo`, so a plate reading "entry" sat up to a full zone height away from
      // the price it named: the exact rule this file already enforces for the inducement -- A
      // CHART ELEMENT MUST NEVER ASSERT A PRICE IT IS NOT DRAWN AT.
      //
      // `wait` is what the band MEANS: a price the detector expects a reaction at, so there is
      // nothing to do until price arrives. It asserts no price at all, which is why it is honest
      // on a plate anchored at one edge while the tradeable level is the other. The dashed line
      // inside the box is where the entry actually is, and it is drawn AT that price.
      //
      // SHORTER THAN WHAT IT REPLACES (`wait long OB` is 12 chars vs `setup long OB` at 13), which
      // matters: this plate is 10px text and a wider plate is withheld more often by
      // `dropIfObstructed`. Direction and zone kind STAY -- an order block and an FVG draw an
      // identical rectangle and are entered differently, which is the caption's other job.
      label: s.live === true ? `wait ${s.is_bull ? 'long' : 'short'} ${kind}` : '',
    });
  }
  return out;
}

/**
 * THE INDUCEMENT IN FORCE, AS ONE DASHED LEVEL TO THE RIGHT EDGE, PLATED `IDM <tf>` AT THAT EDGE.
 *
 * ONE PER PANE, AT MOST -- Andrei, 2026-08-25 (card `t_ec068c65`): "у нас на графике 2 IDM-а.
 * Должен быть один... В его разметке IDM это не статичная точка, а живой уровень: в текущем
 * импульсе на данном таймфрейме актуальный IDM всегда один, но он постоянно переносится по мере
 * движения цены." And: "Т.к. у нас каждый таймфрейм на отдельном pane, видеть на графике мы можем
 * только один IDM."
 *
 * THAT IS PER PANE, NOT PER APP. He is explicit that different timeframes legitimately carry
 * different inducements ("У 4h своя структура и свой IDM, у 15m своя"), and each pane holds one
 * timeframe -- so this function answers for ONE payload and says nothing about the others.
 *
 * WHAT IT USED TO DO, AND THE MEASUREMENT THAT ENDED IT. It looped `p.setups` and emitted one
 * level per LIVE setup, i.e. it read "the setup's IDM" as "the impulse's IDM". On this repo's own
 * `setupxyz15m.fixture.ts` (a real server response: 302 setups, 14 live) that is FOURTEEN grey
 * `IDM` plates from one payload; Andrei's 15m screenshot recorded NINE. The detector was never
 * wrong -- one inducement per setup IS its model, and `ict/` is untouched by this change.
 *
 * SO THE LIVE LEVEL COMES FROM `p.live_idm`, WHICH IS A DIFFERENT OBJECT AND HAD TO BE
 * (measured before writing, card comment 2026-08-25). `ict.setup.find_setups` opens with
 *
 *     for idm in inducements:
 *         if not idm.swept: continue      # "an inducement that still stands has not induced"
 *
 * so a setup row's inducement is ALWAYS already swept -- 14 of 14 live rows and 302 of 302 rows on
 * that fixture carry a non-null `swept_time`. Filtering `p.setups` for an UN-swept inducement
 * therefore selects zero on every market and every timeframe, which would switch the layer off
 * rather than fix it. The server computes the standing level from `ict.idm.find_inducements`'
 * live-edge path instead; see `chartapp/api/overlays.py::_live_idm` for the source quotes.
 *
 * THE SOURCE'S OWN CARRY RULE, verbatim from `docs/dobrunia/Самый ПРАВИЛЬНЫЙ способ находить IDM
 * в SMART MONEY.txt` [04:12], translated: "we break the high and look for the first valid
 * pullback; if there is one in that move we place the IDM there; we break higher, we CARRY the
 * IDM, and we keep carrying it UNTIL WE TAKE THAT MOVE."
 *
 * AN EMPTY LIST IS A CORRECT ANSWER AND IS NOT A FAILURE. Andrei: "если IDM снят и нового импульса
 * ещё нет — не рисовать НИЧЕГО. Пустота честнее устаревшего уровня." The MAJORITY of cached
 * (asset, tf) pairs draw none -- 31 of 45 when this landed, 75 of 114 when review round 1
 * re-measured it. The share is not pinned here on purpose: it moves with the cache, and a count
 * nobody can keep true is the §0b.1 stale-comment defect. Recompute with
 * `process/reviews/t_ec068c65/probe_live_idm_population.py`.
 *
 * THE FORM, and which half of the reference it no longer follows (card `t_0cdae4dc`, 2026-10-11):
 *
 *   * A DEGENERATE BAND, not a fourth primitive. `lo === hi` makes the rectangle a line
 *     (`bandRect` floors the height at one device pixel), so the line is at the inducement price
 *     BY CONSTRUCTION -- the exact defect the old caption had (median 87, worst 597 USD off).
 *   * TO THE RIGHT EDGE, NOT A "short horizontal segment" -- the reference's legend
 *     (`docs/reports/structure-spec-2026-08-10.md` section 2) is overridden here, measured: the
 *     44px stub it produced was on Andrei's prod screen on BTC/15m, ETH/1h and xyz:XYZ100/15m and
 *     he asked why he never sees an IDM. Dobrunia's carry rule [04:12] makes the long form the
 *     faithful one: the level is carried WITH PRICE until it is taken, so it holds on every bar to
 *     the live edge. The plate is at the right edge for the same reason (`plateRight`).
 *   * Not a coloured dot with text -- the same spec table records that his DOTS carry no text at
 *     all, so a worded dot competed with the one primitive that must stay wordless.
 *   * FROM ITS OWN BAR: `live_idm.time`, not any zone's `time`. The inducement is a different
 *     event at a different bar; drawing it on a zone's bar would be a wrong claim about when it
 *     existed.
 *
 * *** WHY THIS IS ITS OWN FUNCTION AND NOT APPENDED INSIDE `setupBands`. *** It was written that
 * way and it broke 15 tests across five files -- `entryline`, `declutter`, `setup`,
 * `setupdistance`, `idmcaption` -- every one a COUNT assertion on what `setupBands` returns
 * ("expected length 1, got 2"). Those counts are not incidental: they pin the spent rectangle
 * surviving (53ba07a), the proximal-edge entry line (8932efa) and the live-only caption cut, all
 * decisions Andrei made personally. Mixing a second KIND of object into that return value makes
 * "how many zones are there" unanswerable and every one of those guards ambiguous.
 * One function, one kind of thing.
 *
 * AND IT NO LONGER READS `p.setups` AT ALL, which is the property that makes "one per pane"
 * structural rather than a filter that could be relaxed: there is no loop here to grow back.
 */
export function idmLevels(
  p: SetupPayload,
  windowFirstTime: number | null = null,
  tf: string | null = null,
): ZoneBand[] {
  const live = p.live_idm;
  // A payload predating this field, or a server answering "nothing is in force", both render as
  // an empty layer. `?? null` rather than a truthiness test: `live_idm` is a mapping and an
  // absent key must not be distinguishable from an explicit null at the draw site.
  if (live === undefined || live === null) return [];
  // FINITE, not merely non-null: `=== null` is not a NaN test (R4-5's predicate, applied to the
  // same field's other price). A NaN level would draw a degenerate band whose plate names a
  // price the detector never produced.
  if (live.time === null || live.level === null || !Number.isFinite(live.level)) return [];
  // AN ANCHOR OLDER THAN THE PANE'S WINDOW IS CLAMPED, NEVER DROPPED (card `t_79d313f6`).
  //
  // The server computes over FULL history while the pane holds a window (600 bars, 960 on 15m),
  // and `RangePrimitive._at` gets `null` from `timeToCoordinate` for a time before the series
  // starts -- so the whole band vanished. Measured over the 104 cached (asset, tf) panes AFTER
  // the server-side fix: 90 answered a level and 7 of those anchored off-window (xyz:XYZ100/15m
  // at 2,136 bars, AVAX/1D at 2,054), i.e. the layer stayed blank on 7.8% of the answers for a
  // reason that has nothing to do with the detector.
  //
  // Clamping is what `_trim_by_time` already decided for the same class of object: a STILL-LIVE
  // level whose run scrolled off the left "is still the price a trader acts on", and an unswept
  // inducement is exactly that -- its `swept` is false, so it applies to every visible bar. The
  // PRICE is untouched; only the segment's left edge moves to the first bar the pane holds, which
  // is where `_at`'s own docstring says a clamp belongs ("the level still applies to every visible
  // bar, so it is truncated. Dropping it would read as 'there is no level here'").
  //
  // `null` means the caller does not know the window (every unit test, and `main.ts` before the
  // first bar load), and then the anchor is left alone rather than guessed at.
  const fromTime = windowFirstTime !== null && Number.isFinite(windowFirstTime)
    ? (Math.max(live.time, windowFirstTime) as UTCTimestamp)
    : live.time;
  return [{
    lo: live.level,
    hi: live.level,
    fromTime,
    // TO THE RIGHT EDGE (card `t_0cdae4dc`). It was a 44-pixel stub at the anchor bar, and the
    // anchor is usually far left of the live edge (and, after the window clamp above, often AT the
    // pane's left edge) -- so the one level a trader acts on sat half a screen from price. The
    // carry rule says what the line is: "we CARRY the IDM ... UNTIL WE TAKE THAT MOVE" [04:12] --
    // it is in force on every bar from its anchor to now, and `null` is how every still-live band
    // here says "runs to the right edge".
    toTime: null,
    fill: IDM_LEVEL_COLOR,
    border: IDM_LEVEL_COLOR,
    // DASHED, so a degenerate band cannot be mistaken for a zone edge or a range ray.
    dashed: true,
    // THE PLATE AT THE RIGHT EDGE, where the eye already is (next to the last candle and the axis
    // price), and drawn even when it collides -- it is a LEVEL, and a dropped level label reads as
    // "there is no level here" (`placePlate`'s own rule).
    plateRight: true,
    // `IDM <tf>` -- NO PRICE IN THE TEXT. The plate sits ON the level, so the axis and the line
    // say the number between them. The TIMEFRAME is the one thing the position does not say, and
    // `idmLabel` is the same word the inducement dot carries (`chartpane.dotStyle`).
    label: idmLabel(tf),
    // No bisector: a line has no middle to halve.
    midline: null,
  }];
}

/** The inducement's word: `IDM 15m`, or bare `IDM` when the caller has no timeframe (unit tests).
 *  The same `IDM ${tf}` the inducement dot carries (`chartpane.dotStyle`), so the plate and the dot
 *  name the one level the same way. */
export function idmLabel(tf: string | null = null): string {
  return tf ? `IDM ${tf}` : 'IDM';
}

/** Is this band the inducement level? By its WORD, because `ZoneBand` has no kind field and the
 *  word is now `IDM <tf>` -- a `label === 'IDM'` test would silently count zero on every pane. */
export const isIdmBand = (b: { label: string }): boolean => /^IDM( |$)/.test(b.label);

/** WHY NO IDM IS DRAWN, as one human line -- or null when one is drawn.
 *
 *  Card `t_0cdae4dc`: an absent level is legitimately the answer on 31 of 117 readable
 *  `bars_cache` series (2026-10-11: 21 no break yet, 10 ladder swept --
 *  `process/reviews/t_0cdae4dc/probe_absence.py`), and with nothing on screen and nothing said,
 *  "no inducement in force" looked identical to "the layer is broken".
 *  The reason is the SERVER's (`live_idm_none`); this only words it, and an unknown or absent code
 *  says "none" rather than guessing one. */
export function idmStatus(p: SetupPayload, tf: string | null = null): string | null {
  if (idmLevels(p).length) return null;
  const head = `${idmLabel(tf)}: none`;
  switch (p.live_idm_none) {
    case 'ladder_swept': return `${head} — ladder swept`;
    case 'no_break': return `${head} — no break yet`;
    case 'too_few_bars': return `${head} — too few bars`;
    default: return head;
  }
}


/**
 * THE ONE SETUP PER DIRECTION A READER CAN ACT ON, and nothing else.
 *
 * Andrei, 2026-08-24 (card t_0ad264de), on `xyz:XYZ100` with ONLY the `setup` layer ticked --
 * i.e. after t_74464b11 had already turned the seven diagnostic layers off. Measured off the
 * screenshot by pixel: 1W drew 1 box / 0 captions, 1D drew ~5 boxes / 2 captions, and the 15m
 * pane drew ~12-14 boxes and ~18 captions -- `wait short OB` SEVEN times in the 29350-29700
 * band, whose rectangles merge into one red slab with stepped edges, plus `IDM` NINE times, with
 * the teal long zones in the same price region. Nothing on the pane said which of them mattered.
 *
 * Re-measured from the route rather than from the picture, and CAPTURED as
 * `setupxyz15m.fixture.ts` so the tests run on the server's own bytes
 * (`overlays.setup('xyz:XYZ100','15m', inversion_rule='traded_through')`, spot 28952, ATR 49.64):
 * 302 setups, 14 LIVE -- 8 short and 6 long -- and the eight shorts sit at 8.10, 8.10, 8.80,
 * 10.98, 13.28, 13.28, 13.28 and 21.49 xATR from spot. THREE of those are the SAME zone
 * (29611-29657) reached through three different inducements and two more are the same
 * 29354-29413 zone; so the slab is not eight opportunities, it is five price levels drawn eight
 * times. (An earlier read the same morning at spot 29158 reported 15 live, the extra one a bull
 * FVG at 29034-29054 that price has since traded through -- the payload moves, which is why the
 * fixture is a dated capture and not a claim about the market.)
 *
 * WHAT IS KEPT: for each direction, the LIVE setup whose zone is NEAREST to spot -- the first
 * one price would reach, hence the only one that can be acted on next. At most two rows survive,
 * so a pane draws at most two boxes and two entry lines.
 *
 * THE `IDM NINE TIMES` HALF OF THAT SCREENSHOT IS NO LONGER THIS FUNCTION'S BUSINESS (card
 * `t_ec068c65`, 2026-08-25). It used to be: `idmLevels` emitted one level per LIVE setup, so
 * filtering the payload to two rows was also what cut nine plates to two. Andrei came back with
 * "у нас на графике 2 IDM-а. Должен быть один", and the answer was to fix it at the source --
 * `idmLevels` now draws THE ONE inducement in force, read from `payload.live_idm`, so it does not
 * scale with the rows and `setupDrawing` adds it once to BOTH views. This function is purely about
 * the ZONES again.
 *
 * WHAT IS WITHHELD IS NOT DELETED: the caller draws the whole payload again on one click (see
 * `renderPaneSetupView` in `main.ts`), the same shape as the `zone(s) off screen -> show them`
 * control this pane already carries. That includes the SPENT rectangles, which Andrei asked for
 * on 2026-08-09 ("the transparent trading area should ALWAYS display") -- honoured behind the
 * click rather than in the default view, because 12 of the 14 boxes on his 15m pane were the
 * ones burying the two that mattered. The default view answers "what do I do now"; the full view
 * answers "where has price reacted before", and one click separates them.
 *
 * DISTANCE IS `zoneDistanceAtr` -- THE SAME FUNCTION THE REACH FILTER USES, deliberately, so
 * "nearest" and "near enough to draw" cannot disagree about what near means. When ATR is
 * unusable that function answers `null` (never Infinity, so it cannot silently empty the
 * overlay); the fallback here is the raw price gap to the near edge, which orders the same way
 * without the ATR normalisation. A setup with a non-finite edge or no `time` cannot be drawn at
 * all (`setupBands` drops it), so it is never a primary candidate -- otherwise the primary slot
 * would be spent on a row that draws nothing and the pane would look empty with `show all (14)`
 * beside it.
 *
 * A NULL `is_bull` HAS NO DIRECTION and is therefore withheld: "one setup per direction" cannot
 * name a side for it, and inventing one would put a long's box on a short's reasoning.
 *
 * TIES ARE BROKEN DETERMINISTICALLY, and this market has them: the two 29354-29413 shorts are
 * both 8.10xATR away. The MOST RECENT `time` wins (a setup formed later supersedes an older one
 * at the same price), then payload order. A non-deterministic pick would make the screenshot
 * evidence in this card unreproducible.
 */
export function primarySetups(p: SetupPayload): SetupPayload {
  const { spot, atr } = p.params;
  /** Sortable distance for a candidate, smaller = reached first. */
  const reach = (s: SetupPayload['setups'][number]): number => {
    const lo = s.zone_lo as number;
    const hi = s.zone_hi as number;
    const d = zoneDistanceAtr(lo, hi, spot ?? NaN, atr ?? NaN);
    if (d !== null) return d;
    if (spot === null || !Number.isFinite(spot)) return 0;
    return spot > hi ? spot - hi : spot < lo ? lo - spot : 0;
  };
  const best = new Map<'bull' | 'bear', SetupPayload['setups'][number]>();
  for (const s of p.setups) {
    if (s.live !== true) continue;
    if (s.is_bull !== true && s.is_bull !== false) continue;
    // DRAWABLE OR NOT A CANDIDATE — the same three refusals `setupBands` makes.
    if (s.zone_lo === null || !Number.isFinite(s.zone_lo)) continue;
    if (s.zone_hi === null || !Number.isFinite(s.zone_hi)) continue;
    if (s.time === null) continue;
    const side = s.is_bull ? 'bull' : 'bear';
    const cur = best.get(side);
    if (cur === undefined) { best.set(side, s); continue; }
    const dNew = reach(s);
    const dCur = reach(cur);
    if (dNew < dCur) { best.set(side, s); continue; }
    if (dNew === dCur && (s.time ?? 0) > (cur.time ?? 0)) best.set(side, s);
  }
  // PAYLOAD ORDER PRESERVED, not `[...best.values()]`: the primitive draws in list order and a
  // Map's insertion order depends on which side happened to appear first in the response.
  const kept = new Set(best.values());
  // COUNTS ARE CARRIED THROUGH UNCHANGED. `setupSummary` prints `live / total` and `spent`, and
  // those numbers are about the MARKET, not about this view. Recomputing them from the two
  // surviving rows would make the panel say `live / total 2 / 2` and hide exactly what the
  // `show all` control exists to disclose.
  return { ...p, setups: p.setups.filter((s) => kept.has(s)) };
}

/** Everything the setup overlay draws for ONE pane, and what it declined to draw.
 *
 *  ONE RETURN VALUE for the same reason `LiquidityDrawing` is one: the reported defect there was
 *  two functions over one payload disagreeing (245 sweep arrows pointing at pools that were never
 *  drawn). Here the pair that must not drift is the GEOMETRY and the number the `show all` control
 *  prints beside it -- a count derived by re-filtering the payload would say 12 while 14 boxes are
 *  on screen, and this repo has shipped exactly that ("34 zone(s) off screen" over 29 painted
 *  zones, `main.ts:2762`).
 */
export type SetupDrawing = {
  /** The zone boxes and the IDM levels, in draw order. */
  bands: ZoneBand[];
  /** How many bands the FULL payload would have drawn that this one does not.
   *
   *  MEASURED BY CALLING THE DRAW FUNCTIONS ON THE FULL PAYLOAD, never by counting payload rows:
   *  a row can be dropped by the reach filter or by a non-finite edge, and both of those are
   *  invisible to a row count. 0 means the pane is already showing everything. */
  withheld: number;
};

/**
 * The setup layer's geometry, in PRIMARY-ONLY (default) or FULL view.
 *
 * THE DECISION LIVES HERE, NOT IN `main.ts`, and that is the lesson of card t_74464b11 one
 * function over: that card's target was a `main.ts` field with NO consumer, guarded for four weeks
 * by a source-text assertion. `main.ts` has no test harness, so a policy expressed there is a
 * policy nothing can hold. `refreshOverlaysOnce` calls this and draws what it returns.
 *
 * `forced` is the pane's own `show all` state -- per pane, because the three panes hold different
 * timeframes and 1W being readable says nothing about 15m (the same per-pane rule the zone-view
 * control learned the hard way; see `renderPaneZoneView`).
 */
export function setupDrawing(
  p: SetupPayload,
  forced: boolean,
  maxDistanceAtr = 20,
  windowFirstTime: number | null = null,
  tf: string | null = null,
): SetupDrawing {
  // THE LIVE IDM IS DRAWN ONCE AND IS NEVER WITHHELD (card `t_ec068c65`, 2026-08-25).
  //
  // It used to be computed twice -- `idmLevels(p)` for the full view and `idmLevels(primary)` for
  // the default one -- because it was emitted PER SETUP, so the primary-only cut had to shrink it
  // too or a two-box pane would carry nine grey plates. It is now ONE level per pane read from
  // `p.live_idm` and has nothing to do with which setups are surfaced, so a second call would be
  // a second chance to disagree about the one thing there is only one of. `primarySetups` copies
  // the payload (`{...p, setups: ...}`), so `idmLevels(primary)` would return the identical band
  // and the difference would be silently zero -- true today, and exactly the kind of accident
  // that stops being true after one edit.
  //
  // AND IT IS OUTSIDE THE `withheld` ARITHMETIC. `withheld` answers "how many bands would the FULL
  // payload draw that this one does not", i.e. what the `show all` control reveals. Clicking it
  // reveals SETUPS; the inducement in force is the same single level either way, so counting it on
  // both sides is the only reading that keeps that number honest.
  // AND `windowFirstTime` IS PASSED THROUGH, not re-derived: `idmLevels` clamps an anchor older
  // than the bars this pane loaded (card `t_79d313f6` -- 7 of 90 answering panes anchored off
  // window and drew nothing). The zone bands need no such argument, because `_trim_by_time`
  // already keeps a live zone whose run starts before the window and every zone the server sends
  // has a bar in the array.
  // `tf` names the plate `IDM <tf>` (card `t_0cdae4dc`), the same words the pane's dot carries.
  const idm = idmLevels(p, windowFirstTime, tf);
  const fullSetups = setupBands(p, undefined, maxDistanceAtr);
  if (forced) return { bands: [...fullSetups, ...idm], withheld: 0 };
  const primary = setupBands(primarySetups(p), undefined, maxDistanceAtr);
  return { bands: [...primary, ...idm], withheld: fullSetups.length - primary.length };
}

export function setupSummary(p: SetupPayload, tf: string | null = null): [string, string][] {
  const c = p.counts;
  // WHY THERE IS NO IDM, when there is none (card `t_0cdae4dc`, 2026-10-11): the chart draws
  // nothing then, and a blank pane cannot say whether the detector refused or the market has no
  // inducement. The row exists ONLY in that case -- a drawn IDM speaks for itself.
  const idmNone = idmStatus(p, tf);
  return [
    ...(idmNone ? ([['IDM', idmNone.replace(/^IDM( \S+)?: /, '')]] as [string, string][]) : []),
    ['source', p.source],
    // LIVE FIRST: the count of setups you can still take is the only number a trader acts on.
    ['live / total', `${c.live} / ${c.total}`],
    ...(p.counts_all && p.counts_all.total !== c.total
      ? ([['whole market', String(p.counts_all.total)]] as [string, string][])
      : []),
    ['spent', String(c.spent)],
    ['long / short', `${c.bull} / ${c.bear}`],
    // WHICH zone kind, in aggregate. The two are drawn with the same rectangle, so without
    // this row the panel cannot say what the boxes on screen actually are.
    // NOT 'OB / FVG entry'. The box is not the entry -- the entry is its NEAR EDGE, the dashed
    // line inside it. Same correction as the band caption above.
    ['OB / FVG zones', `${c.order_block} / ${c.fvg}`],
    // NO 'of breaks' FUNNEL ROW. It read `counts.breaks`, which the builder no longer serves:
    // that counter came from a `breaks_` local the route stopped computing once the seam moved to
    // `setups_from_bars`. A row sourced from a field the API does not send is a row that renders
    // 'undefined' -- and the /breaks overlay already answers "how many breaks are there".
  ];
}

export function fvgSummary(p: FvgPayload, price: number | null | undefined,
  bars: readonly { time: number; high: number; low: number }[] = []): [string, string][] {
  p = liveFvgPayload(p, bars);
  const live = p.gaps.filter(unfilled).length;
  // Counted from the SAME function that draws, not re-derived from the gap list: an
  // inverted-but-unfilled gap becomes TWO bands (virgin phase + inverted phase), so a
  // second count of "one band per gap" would under-report the boxes on screen. This is
  // the recompute-vs-read-what-was-rendered trap, one level up.
  const boxes = fvgBands(p, price).length;
  const selected = nearestFvgs(p, price).length;
  return [
    ['source', p.source],
    ['rule', p.params.inversion_rule],
    ['candles', p.params.candle_basis === 'non_inside' ? 'non-inside (grey candles skipped at formation)' : 'consecutive'],
    ['gaps', String(p.counts.total)],
    ['bull / bear', `${p.counts.bull} / ${p.counts.bear}`],
    ['filled', String(p.counts.filled)],
    ['inverted', String(p.counts.inverted)],
    // Selected gaps and phase boxes differ when a selected gap is inverted.
    ['drawn', `${boxes} box(es) for ${selected} of ${live} unfilled gaps (nearest above/below)`],
  ];
}

// ---------------------------------------------------------------- ranges
/**
 * One dealing range, at ONE swing degree.
 *
 * `unusable` is a SENTENCE, not a flag: an unusable range has a reason (no confirmed
 * swing of one kind yet, or an inverted pair) and printing it is the difference
 * between "the detector refused" and "the detector found nothing".
 */
/** A provisional extreme standing in for a confirmed pivot at one edge of the box.
 *
 *  `of` names WHICH provisional thing it is, because the two differ in kind: an
 *  `open_leg` extreme is bars the detector has seen but not yet labelled a swing, while
 *  a `pending_major` HAS printed on the chart (as a zigzag vertex; its `MH?`/`ML?` text
 *  and dimmed-yellow marker were retired 2026-08-23, t_13260999, and the only dimmed
 *  yellow left is the `confirm_time` circle behind `show-confirm`) and merely
 *  awaits its confirming break. Both can move on the next bar; neither is a swing. */
export type RangeExtension = {
  kind: 'high' | 'low';
  idx: number;
  time: UTCTimestamp;
  price: number;
  /** The fib anchor. The range is WICK-anchored and the OTE fib BODY-anchored —
   *  doctrine, per `ict/ranges.py` — so the extension supplies both. */
  body_price: number;
  of: 'open_leg' | 'pending_major';
  /** `open_leg` only: closed bars since the last confirmed pivot. */
  bars?: number;
};

export type RangeDegree = {
  degree: 1 | 2;
  /** 'local' (SH-SL) | 'major' (MH-ML). */
  label: string;
  /** Which markers frame this box, e.g. 'SH-SL'. */
  anchors: string;
  /** Degree-2 majors at the right edge excluded from the box (break not confirmed). */
  pending_excluded: number;
  /** Those same majors WITH prices and distance. A bare count read `1` on 112 of 117
   *  cached series — a false clean signal; the price and distance are the actionable
   *  part. */
  pending?: {
    kind: 'high' | 'low'; idx: number; price: number;
    time: UTCTimestamp | null; pct_from_spot: number | null; bars_ago: number;
  }[];
  range: {
    hi: number; lo: number; hi_idx: number; lo_idx: number;
    hi_time: UTCTimestamp; lo_time: UTCTimestamp;
    equilibrium: number; eq_time: UTCTimestamp;
  } | null;
  /** Which edges rest on a PROVISIONAL extreme, and the confirmed pair they replaced.
   *
   *  Null when neither edge was extended. An extended edge can move on the next bar —
   *  it is the open leg's running extreme or an unconfirmed major — so the chart draws
   *  it differently and says which pivot pair it superseded. Without this the box would
   *  present a provisional level as a confirmed swing, which is the dishonesty the
   *  `major_pending` dimmed yellow already avoided for markers before that marker was
   *  retired (2026-08-23, t_13260999 — a provisional vertex is now hollow instead). */
  extended: {
    high: RangeExtension | null;
    low: RangeExtension | null;
    confirmed: {
      hi: number; lo: number; hi_idx: number; lo_idx: number;
      hi_time: UTCTimestamp; lo_time: UTCTimestamp;
    };
  } | null;
  /** The DIRECTED leg the OTE fib is measured on. Null when the range is unusable. */
  leg: {
    direction: 'bullish' | 'bearish';
    origin_idx: number;
    terminus_idx: number;
  } | null;
  /** Body-anchored (doctrine). `from_time` = the leg's tip, where the retrace begins. */
  ote: { lo: number; hi: number; from_time: UTCTimestamp; spot_in_ote: boolean } | null;
  /** Wick-anchored fork of the same band. Reported in the sidebar, not drawn. */
  ote_wick: { lo: number; hi: number; spot_in_ote: boolean } | null;
  grade: { zone: string; pct: number; spot: number } | null;
  unusable: string | null;
};

export type RangesPayload = {
  component: 'ranges';
  source: string;
  feed: string;
  params: Record<string, never>;
  /** Both degrees, always: they pick a DIFFERENT (hi, lo) pair on 107 of 107 series. */
  degrees: RangeDegree[];
};

/**
 * A level drawn from the bar that SET it, rightwards. Never across the whole pane.
 *
 * Andrei, 2026-07-27: "there's no need to draw horizontal lines across the entire
 * overlay. It is enough to do it from the respected high/low to the right." A full-width
 * line also makes a false claim — the level did not exist before the swing that set it,
 * and `createPriceLine` (what this replaces) can only draw full-width.
 */
export type LevelRay = {
  price: number;
  /** The bar that set this level. Nothing is drawn to the left of it. */
  fromTime: UTCTimestamp;
  color: string;
  label: string;
  width: number;
  dashed: boolean;
};

/** A filled price band, drawn from `fromTime` rightwards — or bounded at `toTime`.
 *
 *  `toTime` is OPTIONAL and null means "still live, run to the right edge". A band
 *  that ENDED (an FVG filled at bar N) must stop there: drawing it onward claims a
 *  zone that no longer exists, which is the same false claim this primitive rejects
 *  for full-width lines. The OTE band passes null — a retracement zone framed by
 *  the current range has no end bar.
 *
 *  `midline` is also optional: the FVG's CE (consequent encroachment) is drawn as a
 *  dashed bisector per Andrei's reference diagram, where the box gives the zone and
 *  the midline gives the entry. The OTE band has no midline (its own edges ARE the
 *  62/79 levels).
 */
export type ZoneBand = {
  lo: number;
  hi: number;
  fromTime: UTCTimestamp;
  /** EMPTY MEANS "DRAW THE BOX, PRINT NOTHING". See `setupBands`: a spent zone is audit
   *  evidence whose rectangle must stay (that is what 2026-08-09 fixed), while its caption
   *  is what made the 15m pane an illegible mat of 34 overlapping strings. `RangePrimitive`
   *  skips an empty caption and does not count it as suppressed -- it was never a candidate. */
  /** Right bound, or null to run to the pane's right edge. */
  toTime?: UTCTimestamp | null;
  fill: string;
  border: string;
  label: string;
  /** Dashed bisector price (FVG CE), or null for none. */
  midline?: number | null;
  /** Stroke the band's edges dashed. For a LEVEL drawn as a degenerate band (the live IDM), so it
   *  cannot be read as a zone's solid edge. */
  dashed?: boolean;
  /** Put the plate at the PANE'S RIGHT EDGE instead of just right of `fromTime`, and never drop
   *  it on a collision. For a level that runs to the live edge: its anchor can be half a screen
   *  from price (card `t_0cdae4dc`), and a level whose label is withheld reads as no level. */
  plateRight?: boolean;
  /** The band's `fromTime`..`toTime` extent is a FACT, not a readability shape: skip the
   *  2x-height aspect floor (`bandRect` rule 8). Session boxes only (card t_5b2017b5). */
  exactSpan?: boolean;
  /** Paint from the top of the pane to the bottom and ignore `lo`/`hi`: a TIME window, not a
   *  price zone (the experiment overlay's `windows[]`, card t_42cdbde6, 2026-10-14). The plate
   *  goes at the band's top-left. A band without the field takes the price path unchanged. */
  fullHeight?: boolean;
};


/**
 * The open leg's running extreme as a LEVEL RAY, so its label is legible.
 *
 * MEASURED, not assumed: the marker alone was not enough. lightweight-charts
 * paints marker text at the marker's bar, and the open leg's extreme is by
 * definition within the last few bars — instrumenting canvas `fillText` showed
 * `L?` painted at **x=896 on a 910px canvas**, i.e. 14px of room, clipped and
 * unreadable, while `ML? ` at x=845 was fine. That is the same class of defect as
 * the break labels (which shipped broken three times, all green through
 * tsc+vite): what is IN the marker list is not what is ON the screen.
 *
 * The ray + plate primitive already solves this — it draws from the level's bar
 * rightwards with a legible plate — and the range levels on the same chart prove
 * it. So the open leg gets BOTH: the circle marks WHICH bar set the extreme, the
 * ray carries the price and the `?`.
 */
export function openLegDrawing(p: SwingsPayload): LevelRay[] {
  const ol = p.open_leg;
  if (!ol) return [];
  // A PENDING MAJOR THIS LEG HAS ALREADY BEATEN gets named on the same plate, with both
  // prices (2026-07-28, task t_9d07e429). `ML?`/`MH?` claims to be "the open phase's
  // running extreme" and on 31.6% of cached series it is not — `ict/mss.py` walks the
  // swing SEQUENCE, so the marker holds the best PRINTED swing while price has moved
  // past it (SNDK 1D: `ML? 1309.9` with the leg at 1223.4).
  //
  // BOTH NUMBERS, not a silent replacement. `ict.mss` is correct — a major must be a
  // degree-1 swing — so the marker is not moved and `mss` gains no bars input. Making
  // the relationship VISIBLE is what the ticket asked for over a rule that picks
  // quietly: the reader sees the structural marker, the live extreme, and that one has
  // outrun the other, on one line at the price the leg actually reached.
  const superseded = p.pivots.find(
    (q) => q.superseded_by_open_leg != null && q.kind === ol.kind,
  );
  const beats = superseded
    ? ` — beats ${ol.kind === 'high' ? 'MH' : 'ML'}? ${priceText(superseded.price)}` : '';
  return [{
    price: ol.price,
    fromTime: ol.time,
    color: OPEN_LEG_COLOR,
    label: `${ol.kind === 'high' ? 'H' : 'L'}? ${priceText(ol.price)} `
      + `(open leg, ${ol.bars} bar${ol.bars === 1 ? '' : 's'})${beats}`,
    width: 1,
    dashed: true,
  }];
}

export type RangeDrawing = {
  rays: LevelRay[];
  bands: ZoneBand[];
};

/** Degree-1 range edges keep the candle palette; degree-2 borrows the major yellow. */
const RANGE_COLORS = {
  1: { hi: '#ef5350', lo: '#26a69a', eq: '#787b86' },
  2: { hi: MAJOR_COLOR, lo: MAJOR_COLOR, eq: '#8a7320' },
} as const;

/**
 * OTE fill by leg direction. Transparent so candles and wicks stay readable through it.
 *
 * Andrei: "Let the bearish OTE zone to be transparent red background and bullish OTE
 * zone to have transparent green bagground." Direction comes from the API's `leg`
 * (derived from which range edge is NEWER) — this file does not decide it.
 *
 * ALPHA RAISED 0.13 -> 0.26 (2026-07-28), measured, not preferred. The same invisibility
 * the FVG boxes hit: sampling the composited canvas on xyz:SNDK 1D gave `rgb(47,31,40)`
 * inside a bearish band against a `rgb(19,23,34)` background — a delta of 28, and a
 * vision pass over the live chart reported "no clearly rendered transparent-red fill
 * zone anywhere" while two bands sat on-pane in the draw list. A widened dealing range
 * makes it worse, not better: the band is a fixed 17% of the range height, so the
 * degree-1 band on that series is **5.2 device pixels tall** — 0.13 over five pixels is
 * nothing. Half the FVG's 0.30 would have been invisible for the same reason; 0.26 is
 * the FVG value less the fact that OTE bands cannot stack (one per degree, and the two
 * degrees pick a different pair on 107 of 107 series).
 */
const OTE_FILL = {
  bullish: { fill: 'rgba(38, 166, 154, 0.26)', border: 'rgba(38, 166, 154, 0.75)' },
  bearish: { fill: 'rgba(239, 83, 80, 0.26)', border: 'rgba(239, 83, 80, 0.75)' },
} as const;

/**
 * SWEPT-RANGE presentation. A range whose spot has traded THROUGH it
 * (`grade.zone === 'outside'`) is stale as a decision tool but not worthless as
 * information — it is where price broke FROM. Measured over the local cache:
 * `outside` on 7 of 18 degree-1 series (38.9%) and 6 of 18 degree-2 (33.3%),
 * extremes BTC 1W deg2 at −219.9% and xyz:SNDK 1D deg2 at −119.7%. The backend
 * already computed that verdict; the frontend drew the box anyway, projecting an
 * OTE retracement band onto a range price had left — the state in Andrei's
 * screenshot (SL 1309.9 drawn with spot at 1233.20).
 *
 * The choice made here, and why it is not suppression: the LEVELS stay (dimmed,
 * labelled `swept`) because "where price broke from" is real; the OTE BAND is
 * dropped, because a 62-79% retracement of a range that no longer contains price
 * is a zone nobody can act on. Silently hiding the whole overlay would be the
 * same failure class as the panel once rendering a failed dex read as a calm "No
 * open positions" — so the sidebar states it, per kind, in `rangesSummary`.
 */
const SWEPT_DIM = 0.45;

/** Blend a hex colour toward the chart background — a dimmed level, not a hidden one. */
function dim(hex: string, amount = SWEPT_DIM): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const mix = (c: number) => Math.round(c * amount + 0x13 * (1 - amount));
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
}

/**
 * Payload -> geometry. Both degrees, each as rays + one OTE band.
 *
 * Pure translation: every price, every timestamp and the leg direction arrive from the
 * API. What is decided HERE is presentation only — which colour, how thick, what text.
 */
export function rangeDrawing(p: RangesPayload): RangeDrawing {
  const rays: LevelRay[] = [];
  const bands: ZoneBand[] = [];

  for (const d of p.degrees) {
    if (!d.range || !d.leg || !d.ote) continue;      // unusable: reason shown in the sidebar
    const major = d.degree === 2;
    const c = RANGE_COLORS[major ? 2 : 1];
    const w = major ? 2 : 1;
    const [hiTag, loTag] = major ? ['MH', 'ML'] : ['SH', 'SL'];
    // SWEPT: price has traded through this range. The backend already said so
    // (`grade.zone`); this used to be ignored and the box drawn as if live.
    const swept = d.grade?.zone === 'outside';
    const tag = swept ? ' swept' : '';
    const col = (x: string) => (swept ? dim(x) : x);
    // EXTENDED edges rest on a provisional extreme (the open leg's running high/low, or
    // an unconfirmed major). They are drawn DASHED with a `?` and their own provenance,
    // never as a confirmed swing — the same honesty as `major_pending`'s dimmed yellow.
    // Without this a level that can move on the next bar would look like structure.
    const hiExt = d.extended?.high ?? null;
    const loExt = d.extended?.low ?? null;
    // `?` ON THE CHART, THE REASON IN THE PANEL. `MH? (unconfirmed major) 65266` is 28
    // characters of plate on a pane where seven of these pile into one corner over the order
    // block (Andrei, 2026-08-09: "overall layout looks overloaded"), and its parenthetical is
    // constant per source rather than per level -- it never distinguishes one plate from
    // another on screen. `rangesSummary` already prints WHICH edge is provisional, what kind of
    // extreme it rests on, how many bars old it is and the confirmed level it superseded, which
    // is strictly more than the parenthetical said. The `?` and the dashes still mark the level
    // as provisional where it is drawn, so nothing reads as structure that is not.
    const extTag = (e: RangeExtension | null) => (e ? '?' : '');

    rays.push({ price: d.range.hi, fromTime: d.range.hi_time, color: col(c.hi),
                label: `${hiTag}${extTag(hiExt)} ${priceText(d.range.hi)}${tag}`,
                width: w, dashed: !major || swept || hiExt !== null });
    rays.push({ price: d.range.lo, fromTime: d.range.lo_time, color: col(c.lo),
                label: `${loTag}${extTag(loExt)} ${priceText(d.range.lo)}${tag}`,
                width: w, dashed: !major || swept || loExt !== null });
    // EQ starts where the BOX was complete (the later edge), not at either edge on its
    // own: the midpoint of a range is not defined until both of its sides exist.
    rays.push({ price: d.range.equilibrium, fromTime: d.range.eq_time, color: col(c.eq),
                label: `EQ ${d.label}${tag}`, width: 1, dashed: true });

    // The OTE band is DROPPED for a swept range: a 62-79% retracement of a range
    // price has already left is a level nobody can act on, and drawing it was the
    // reported defect. The levels above still show where price broke FROM, and
    // `rangesSummary` states the suppression rather than leaving it to be noticed.
    if (swept) continue;

    const tone = OTE_FILL[d.leg.direction];
    bands.push({
      lo: d.ote.lo, hi: d.ote.hi, fromTime: d.ote.from_time,
      fill: tone.fill, border: tone.border,
      // `OTE local ↑` -- the DEGREE and the leg direction, which are what differ between the two
      // bands on a pane. `62-79%` was in every one of these strings and so distinguished none of
      // them: it is what OTE MEANS, stated once in the panel rather than on every plate. Dropping
      // it and the redundant word for the arrow takes `OTE local up 62-79%` to `OTE local ↑`,
      // which is what let this caption survive the collision test beside the range levels
      // instead of being withheld. The band's own price extent is on the axis.
      label: `OTE ${d.label} ${d.leg.direction === 'bullish' ? '↑' : '↓'}`,
    });
  }
  return { rays, bands };
}

export function rangesSummary(p: RangesPayload): [string, string][] {
  const rows: [string, string][] = [['source', p.source]];
  for (const d of p.degrees) {
    const tag = `${d.label} (${d.anchors})`;
    if (!d.range || !d.leg || !d.ote || !d.grade) {
      // The REASON, not an empty row: a blank here reads as "no range", which is a
      // different fact from "the range is inverted" or "one side never confirmed".
      rows.push([tag, d.unusable ?? 'unusable']);
      continue;
    }
    rows.push([tag, `${priceText(d.range.lo)} – ${priceText(d.range.hi)}`]);
    // WHICH EDGE IS PROVISIONAL, and what it superseded. An extended edge can move on
    // the next bar; presenting the box without saying so would make a provisional level
    // read as structure. The confirmed pair is shown beside it so the box can be tied
    // back to the swings actually drawn on the chart.
    for (const [side, e] of [['hi', d.extended?.high], ['lo', d.extended?.low]] as const) {
      if (!e) continue;
      const was = side === 'hi' ? d.extended!.confirmed.hi : d.extended!.confirmed.lo;
      const which = e.of === 'open_leg'
        ? `open leg${e.bars ? `, ${e.bars} bar${e.bars === 1 ? '' : 's'}` : ''}`
        : 'unconfirmed major';
      rows.push([`└ ${side} extended`,
                 `${priceText(e.price)} (${which}) — confirmed was ${priceText(was)}`]);
    }
    // `equilibrium` is a midpoint, so it leaks IEEE-754 exactly as CE did — 73 of the
    // 157 float-tailed values in this payload. Rounded at the display edge from its own
    // edges, never at the source (downstream code computes with it).
    rows.push([`└ EQ / leg`,
               `${priceText(d.range.equilibrium, d.range.lo, d.range.hi)} / ${d.leg.direction}`]);
    // Body-anchored is doctrine; the wick fork is shown beside it because the two
    // disagree by a median 0.48 band-widths (max 3.33) across the cache.
    //
    // `priceText`, not `toFixed(2)` — the foot-gun its own docstring names, measured here:
    // a fixed 2 places made the band's two edges IDENTICAL text on 58 of 223 rows (26.0%)
    // and printed `0.00 – 0.00` on 51 (22.9%), i.e. every ratio pair's OTE was unreadable.
    // Precision is derived from the band's own edges.
    const ote = (lo: number, hi: number) => `${priceText(lo, lo, hi)} – ${priceText(hi, lo, hi)}`;
    rows.push([
      `└ OTE (body)`,
      // THE RETRACEMENT DEPTH LIVES HERE NOW, once per range, because it came off the chart
      // plates where it appeared on every band and distinguished none of them. Words, not a bare
      // pair of numbers: `62-79%` alone in a value column reads as a price.
      `${ote(d.ote.lo, d.ote.hi)}, the 62-79% retracement`
      + `${d.ote.spot_in_ote ? ' ← spot' : ''}`,
    ]);
    if (d.ote_wick) {
      rows.push([`└ OTE (wick)`, ote(d.ote_wick.lo, d.ote_wick.hi)]);
    }
    // `zone` can be "outside" and `pct` is UNCLAMPED — the legacy form silently
    // graded out-of-range prices as premium/discount.
    rows.push([`└ grade`, `${d.grade.zone} (${d.grade.pct.toFixed(1)}%)`]);
    if (d.grade.zone === 'outside') {
      // DISCLOSED, not silent. The levels are still drawn (dimmed, labelled
      // `swept` — where price broke FROM is real information); the OTE band is
      // not, because a retracement zone of a range price has left is unactionable.
      // A band that simply vanished with no explanation would be the same failure
      // class as a failed read rendering as a calm "no data".
      rows.push([`└ swept`, 'levels dimmed, OTE band not drawn (spot outside the range)']);
    }
    if (d.degree === 2 && d.pending_excluded > 0) {
      // WITH PRICES, or not at all (item 5 of task t_9d07e429). As a bare count this
      // row read `1` on 112 of 117 cached series — it fired on 98.3% and told a reader
      // nothing, the same false-clean-signal class as the deleted `unconfirmed: 0`.
      // What is actionable is WHICH price is withheld and how far it is from spot.
      //
      // NB an excluded major can now also be the thing EXTENDING an edge (see
      // `└ hi/lo extended` above), in which case it is no longer withheld from the box
      // at all — so this row states what is still outside it.
      const list = (d.pending ?? [])
        .map((q) => {
          const dist = q.pct_from_spot == null ? '' : ` ${q.pct_from_spot >= 0 ? '+' : ''}`
            + `${q.pct_from_spot.toFixed(1)}% from spot`;
          return `${q.kind === 'high' ? 'MH?' : 'ML?'} ${priceText(q.price)}${dist}`
            + `, ${q.bars_ago}b ago`;
        })
        .join(' · ');
      rows.push([`└ pending`, list || `${d.pending_excluded} unconfirmed major(s)`]);
    }
  }
  return rows;
}

/**
 * The price range that shows the SERIES and every band handed in, with padding.
 *
 * Extracted as a pure function because it is the whole content of the "show them"
 * control on the summary's `zones off screen` row, and that control cannot be unit
 * tested through a real chart. `lo`/`hi` come from the pane's current visible range
 * (the series' own extent, as autoscale left it), the bands are the ones the primitive
 * refused to draw -- see `ZoneBand`.
 *
 * UNION, NOT REPLACEMENT. Framing the off-screen zone alone would answer "where is my
 * zone" by hiding the price action that gives it meaning; a 7.53 order block on an
 * instrument trading at 73 is only information next to the 73. The candles becoming a
 * thin band at the top is the unavoidable cost of that and is what the user asked for.
 *
 * PADDING IS FRACTIONAL, not absolute: the same control serves SOL at 73 and BTC at
 * 118,000, and a fixed 1.0 would be invisible on one and half the range on the other.
 * Applied to the FINAL span so a band far outside gets breathing room proportional to
 * the zoom it forced, and 0 when nothing widens (an already-visible band must not
 * nudge the view -- that would make a no-op click look like a bug).
 */
/**
 * How many bands are OUTSIDE the visible price window. The honest count behind
 * `N zone(s) off screen`.
 *
 * *** WHY THIS FUNCTION EXISTS -- THE ROOT CAUSE OF "34 zone(s) off screen". ***
 *
 * `main.ts` derived the number by SUBTRACTION off the label counters:
 *
 *     offPane = zoneBands.length - range.drawnBandLabels - range.hiddenBandLabels
 *
 * That was true when every band asked for a caption. It stopped being true the moment
 * `setupBands` started sending an EMPTY label for a spent setup: `RangePrimitive` skips an
 * empty caption and deliberately counts it as neither drawn NOR hidden (it was never a
 * candidate), so every spent zone fell straight through the subtraction into "off screen".
 *
 * MEASURED on the live BTC/15m feed, 600-bar window, against Andrei's own 63,000-66,200 axis:
 * 48 setups served, 19 live, 29 spent. Zones genuinely outside that price window: SEVEN.
 * The subtraction reported THIRTY-SIX. So the warning was counting the caption policy, not
 * the price scale -- 29 of its 36 zones were painted on screen in front of him while the
 * header called them off screen.
 *
 * It is NOT a fetch problem, and that is worth stating because the clamp shipped in 8dbbd1c
 * was aimed there. The fetch is correct: `&first_time=` trims 288 detected setups down to
 * the 48 the window can draw. There was never a payload of 34 stray zones to stop fetching.
 *
 * The fix is to ask the question directly -- compare each band against the window -- which is
 * the same predicate `_drawBands` and the zone-label pass already refuse on, and the same one
 * the `__ictZones` test hook has always used. One rule, one place, no counter arithmetic.
 */
export function offScreenBands(
  visible: { lo: number; hi: number } | null,
  bands: readonly { lo: number; hi: number }[],
): number {
  // No readable price window (a pane with zero height, or before the first paint) is not a
  // licence to claim every zone is missing -- that is the confidently-wrong number this
  // whole function replaces. Nothing is known, so nothing is reported.
  if (visible === null) return 0;
  return bands.filter((b) => b.hi < visible.lo || b.lo > visible.hi).length;
}

export function zoneViewRange(
  visible: { lo: number; hi: number },
  bands: readonly { lo: number; hi: number }[],
  padFrac = 0.04,
): { lo: number; hi: number } {
  let lo = visible.lo;
  let hi = visible.hi;
  for (const b of bands) {
    // Both ends of every band, because a band can straddle one edge of the view.
    lo = Math.min(lo, b.lo, b.hi);
    hi = Math.max(hi, b.lo, b.hi);
  }
  if (lo === visible.lo && hi === visible.hi) return { lo, hi };
  const pad = (hi - lo) * padFrac;
  // Never negative: a price scale with a sub-zero bound draws an axis nobody can read,
  // and every instrument here is positive.
  return { lo: Math.max(0, lo - pad), hi: hi + pad };
}

// The research-only experiment renderer lives in its own pure module (`experiment.ts`, no DOM,
// scanned by navigator.test.ts) and is re-exported here, so main.ts reads every renderer from
// `./overlays` (card t_42cdbde6, Plan Approval decision 1, option A, 2026-10-14).
export { experimentDrawing } from './experiment';
export type { ExperimentPayload, ExperimentEvent, ExperimentDrawing } from './experiment';
